import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import AdminTracking from "@/pages/AdminTracking";
import AdminSequence from "@/pages/AdminSequence";
import AdminMeetings from "@/pages/AdminMeetings";
import AdminNotes from "@/pages/AdminNotes";
import { GripVertical, ChevronUp, ChevronDown, ArrowUp, ArrowDown } from "lucide-react";
import { SITE_PAGES, HARDCODED_REDIRECTS, isReservedPath } from "../sitePages";
import { CampaignLinkBuilder } from "../components/CampaignLinkBuilder";
import { markInternalAdmin } from "../deviceTracking";
import { adminErrorDetail } from "../lib/adminError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "bt_admin_pw";
const EMPTY_REDIRECT_FORM = { id: null, source: "", destination: "", type: 301 };
const DELIVERY_LABELS = { not_sent: "Not sent", sent: "Sent", delivered: "Delivered", failed: "Failed", bounced: "Bounced" };
const FOLLOWUP_LABELS = { not_confirmed: "Not confirmed", active: "Active", unsubscribed: "Unsubscribed", bounced: "Bounced", complained: "Complained" };
const EVENT_LABELS = {
  free_book_signup: "Book requested",
  email_send_attempted: "Email send attempted",
  email_sent: "Email sent",
  email_failed: "Email failed",
  email_delivered: "Email delivered",
  email_bounced: "Email bounced",
  email_complained: "Complaint received",
  book_downloaded: "Book downloaded",
  manual_book_sent: "Record send (manual)",
  resend_book: "Resend book",
  review_submitted: "Review submitted",
  followup_status_changed: "Follow-up status changed",
  verified_download_changed: "Verified download changed",
  lead_deleted: "Lead deleted",
  first_touch_set: "First-touch source set",
  last_touch_updated: "Last-touch source updated",
  token_revoked: "Download link revoked",
  token_reissued: "Download link reissued",
  book_link_reviewed: "Book link reviewed",
  unsubscribed: "Unsubscribed",
  sequence_manual_start: "Sequence started (manual)",
  sequence_send_attempted: "Sequence email send attempted",
  sequence_email_sent: "Sequence email sent",
  sequence_email_delivered: "Sequence email delivered",
  sequence_email_failed: "Sequence email failed",
  sequence_email_bounced: "Sequence email bounced",
  sequence_email_complained: "Sequence complaint received",
  sequence_skip_precutoff: "Sequence skipped — before start date",
  sequence_skip_stale: "Sequence skipped — too old (stale)",
  sequence_skip_waiting: "Sequence waiting for send day",
  sequence_skip_gap: "Sequence held (spacing gap)",
};

const VIA_LABELS = { thankyou: "thank-you page", email: "email link", ebook: "ebook page", unknown: "unknown route" };

const SEQ_VERB = {
  sequence_send_attempted: "send attempted",
  sequence_email_sent: "sent",
  sequence_email_delivered: "delivered",
  sequence_email_failed: "failed",
  sequence_email_bounced: "bounced",
  sequence_email_complained: "complaint received",
};
const eventLabel = (ev) => {
  const n = ev.detail && ev.detail.email_number;
  if (SEQ_VERB[ev.type] && n) return `Sequence email ${n} ${SEQ_VERB[ev.type]}`;
  if (ev.type === "sequence_skip_waiting" && n) return `Sequence email ${n} — waiting for send day`;
  if (ev.type === "sequence_skip_gap" && n) return `Sequence email ${n} — held (spacing gap)`;
  if (ev.type === "book_downloaded") return `Book downloaded — ${VIA_LABELS[(ev.detail && ev.detail.via) || "unknown"]}`;
  return EVENT_LABELS[ev.type] || ev.type;
};
const eventDetailText = (ev) => {
  if (ev.detail && ev.detail.reason) return ev.detail.reason;
  if (ev.detail && ev.detail.message_id) return ev.detail.message_id;
  return (ev.source || "—") + (ev.campaign ? ` · ${ev.campaign}` : "");
};
const dvOf = (l) => l.delivery_status || (l.book_sent_at ? "sent" : "not_sent");
const fuOf = (l) => l.followup_status || "not_confirmed";
// Calendar-day difference in America/New_York (matches the engine's date-diff, not a duration).
const nyDateStr = (d) => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
};
const nyDayDiffFromNow = (iso) => {
  if (!iso) return 0;
  const b = new Date(iso);
  if (isNaN(b.getTime())) return 0;
  const [by, bm, bd] = nyDateStr(b).split("-").map(Number);
  const [ny, nm, nd] = nyDateStr(new Date()).split("-").map(Number);
  return Math.round((Date.UTC(ny, nm - 1, nd) - Date.UTC(by, bm - 1, bd)) / 86400000);
};
const SEQ_STARTABLE = ["excluded_precutoff", "skipped_stale", "hard_failed"];
const DAY_BY_EMAIL_NUMBER = { 1: 1, 2: 5, 3: 10, 4: 15, 5: 20, 6: 25, 7: 30, 8: 40 };
// Resend is always available so the operator can resend the ebook to any lead
// of their choice (a confirmation dialog guards against accidental sends).
const canResend = (l) => true;
const SOURCE_LABELS = { youtube: "YouTube", reddit: "Reddit", instagram: "Instagram", tiktok: "TikTok", google: "Google", bing: "Bing", duckduckgo: "DuckDuckGo", yahoo: "Yahoo", ecosia: "Ecosia" };
const titleize = (s) => s.replace(/[-_]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const utcDayStr = (d) => d.toISOString().slice(0, 10);
const dashPresetRange = (p) => {
  const now = new Date();
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth();
  const pad2 = (n) => String(n).padStart(2, "0");
  const localDay = (offset) => {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - offset);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  };
  if (p === "today") return { from: localDay(0), to: localDay(0) };
  if (p === "yesterday") return { from: localDay(1), to: localDay(1) };
  if (p === "three_days_ago") return { from: localDay(3), to: localDay(3) };
  if (p === "this_week") {
    const dow = (now.getDay() + 6) % 7;
    const mon = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dow);
    return { from: `${mon.getFullYear()}-${pad2(mon.getMonth() + 1)}-${pad2(mon.getDate())}`, to: localDay(0) };
  }
  if (p === "this_month") return { from: utcDayStr(new Date(Date.UTC(y, m, 1))), to: "" };
  if (p === "last_month") return { from: utcDayStr(new Date(Date.UTC(y, m - 1, 1))), to: utcDayStr(new Date(Date.UTC(y, m, 0))) };
  if (p === "last_30") return { from: utcDayStr(new Date(now.getTime() - 29 * 86400000)), to: "" };
  if (p === "this_year") return { from: `${y}-01-01`, to: "" };
  return { from: "", to: "" };
};
const channelLabel = (k) => SOURCE_LABELS[k] || (k.includes(".") ? k : titleize(k));
const SAFE_UTM = /^[a-z0-9._-]+$/i;

const WeeklyGoalLine = ({ current, goal, streak, editing, inputValue, onInputChange, onEdit, onSave, onCancel, saving, error }) => {
  const g = Math.max(0, Number(goal) || 0);
  const cur = Math.max(0, Number(current) || 0);
  const pct = g > 0 ? Math.min(100, Math.round((cur / g) * 100)) : 0;
  const barFill = g > 0 ? Math.min(100, (cur / g) * 100) : 0;
  const remaining = Math.max(0, g - cur);
  const hit = g > 0 && cur >= g;
  return (
    <div className="mb-8 max-w-2xl" data-testid="dash-weekly-goal">
      <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
        <h3 className="label-mono text-muted">Weekly signup goal</h3>
        {!editing ? (
          <button
            type="button"
            onClick={onEdit}
            className="label-mono text-muted underline hover:text-ink"
            data-testid="dash-weekly-goal-edit"
          >
            {g > 0 ? "Edit goal" : "Set goal"}
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              max="100000"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value)}
              className="w-24 px-2 py-1 border border-hairline bg-paper text-ink label-mono"
              placeholder="0"
              data-testid="dash-weekly-goal-input"
              autoFocus
            />
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="label-mono px-3 py-1 border border-ink text-ink hover:bg-ink hover:text-paper disabled:opacity-50"
              data-testid="dash-weekly-goal-save"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="label-mono text-muted underline hover:text-ink"
              data-testid="dash-weekly-goal-cancel"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
      {error ? <p className="label-mono text-sm mb-2" style={{ color: "#B91C1C" }} data-testid="dash-weekly-goal-error">{error}</p> : null}
      {g > 0 ? (
        <div>
          <div className="flex items-baseline justify-between gap-4 mb-2">
            <span className="text-ink" data-testid="dash-weekly-goal-progress">
              {cur} / {g} this week
            </span>
            <span className="flex items-baseline gap-3">
              {streak > 0 && (
                <span className="label-mono text-ink" data-testid="dash-weekly-goal-streak">
                  {streak}-week streak
                </span>
              )}
              <span className="label-mono text-muted" data-testid="dash-weekly-goal-pct">
                {pct}%{hit ? " · hit" : remaining > 0 ? ` · ${remaining} to go` : ""}
              </span>
            </span>
          </div>
          <div
            className="w-full h-2 bg-paper border border-hairline overflow-hidden"
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin="0"
            aria-valuemax="100"
            data-testid="dash-weekly-goal-bar"
          >
            <div
              className="h-full"
              style={{ width: `${barFill}%`, background: "#69d2c7", transition: "width 200ms ease" }}
              data-testid="dash-weekly-goal-bar-fill"
            />
          </div>
        </div>
      ) : (
        !editing && (
          <p className="label-mono text-muted" data-testid="dash-weekly-goal-empty">
            No goal set. Choose a weekly signup target to track progress.
          </p>
        )
      )}
    </div>
  );
};

const SignupsSparkline = ({ days }) => {
  const [hover, setHover] = useState(null);
  if (days.length < 2) return null;
  const peak = Math.max(...days.map((d) => d.count));
  const total = days.reduce((a, d) => a + d.count, 0);
  const W = 600, H = 180, PAD = 4;
  const scale = Math.max(1, peak);
  const xy = days.map((d, i) => [
    (i / (days.length - 1)) * W,
    H - PAD - (d.count / scale) * (H - PAD * 2),
  ]);
  const line = xy.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  const fmtD = (s) => new Date(`${s}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / rect.width;
    setHover(Math.min(days.length - 1, Math.max(0, Math.round(frac * (days.length - 1)))));
  };
  const hoverPct = hover === null ? 0 : Math.min(92, Math.max(8, (xy[hover][0] / W) * 100));
  return (
    <div className="mb-10 max-w-2xl" data-testid="dash-sparkline">
      <h3 className="label-mono text-muted mb-3">Signups per day</h3>
      <div className="border-t border-hairline pt-4">
        <div className="flex items-baseline justify-between gap-4 mb-2 flex-wrap">
          <span className="label-mono text-muted" data-testid="dash-sparkline-range">
            {fmtD(days[0].day)} – {fmtD(days[days.length - 1].day)}
          </span>
          <span className="label-mono text-muted" data-testid="dash-sparkline-summary">
            {total} signup{total === 1 ? "" : "s"}{peak > 0 ? ` · peak ${peak}/day` : ""}
          </span>
        </div>
        <div className="relative" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
          {hover !== null && (
            <div
              className="absolute -top-7 label-mono text-ink bg-paper border border-hairline px-2 py-0.5 whitespace-nowrap -translate-x-1/2 pointer-events-none z-10"
              style={{ left: `${hoverPct}%` }}
              data-testid="dash-sparkline-tooltip"
            >
              {fmtD(days[hover].day)} · {days[hover].count} signup{days[hover].count === 1 ? "" : "s"}
            </div>
          )}
          <svg
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
            className="w-full h-48 block"
            role="img"
            aria-label="Signups per day"
            data-testid="dash-sparkline-svg"
          >
            <polygon points={area} fill="#69d2c7" opacity="0.12" />
            <polyline points={line} fill="none" stroke="#69d2c7" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
            {hover !== null && (
              <line x1={xy[hover][0]} y1={0} x2={xy[hover][0]} y2={H} stroke="#0A0A0A" strokeWidth="1" strokeDasharray="3 3" vectorEffect="non-scaling-stroke" />
            )}
          </svg>
        </div>
      </div>
    </div>
  );
};

export default function Admin() {
  const [pw, setPw] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(() =>
    window.location.pathname.replace(/\/+$/, "") === "/admin/tracking" ? "tracking" : "contacts"
  );
  const [leadSent, setLeadSent] = useState("all");
  const [leadDownload, setLeadDownload] = useState("all");
  const [leadSource, setLeadSource] = useState("all");
  const [leadUnsub, setLeadUnsub] = useState("all");
  const [leadVia, setLeadVia] = useState("all");
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [redirects, setRedirects] = useState([]);
  const [redirectForm, setRedirectForm] = useState(EMPTY_REDIRECT_FORM);
  const [redirectFormError, setRedirectFormError] = useState("");
  const [deleteRedirectTarget, setDeleteRedirectTarget] = useState(null);
  const [csrf, setCsrf] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteContactTarget, setDeleteContactTarget] = useState(null);
  const [markSentTarget, setMarkSentTarget] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [previewEmailLinks, setPreviewEmailLinks] = useState(false);
  const [bookLinks, setBookLinks] = useState([]);
  const [blRevokeConfirm, setBlRevokeConfirm] = useState(null);
  const [dashFrom, setDashFrom] = useState("");
  const [dashTo, setDashTo] = useState("");
  const [dashPreset, setDashPreset] = useState("all");
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [contactSearch, setContactSearch] = useState("");
  const [leadQ, setLeadQ] = useState("");
  const [leadDatePreset, setLeadDatePreset] = useState("all");
  const [leadFrom, setLeadFrom] = useState("");
  const [leadTo, setLeadTo] = useState("");
  const [leadFromText, setLeadFromText] = useState("");
  const [leadToText, setLeadToText] = useState("");
  const [expandedLead, setExpandedLead] = useState(null);
  const [leadHistory, setLeadHistory] = useState({});
  const [resendTarget, setResendTarget] = useState(null);
  const [seqStartTarget, setSeqStartTarget] = useState(null);
  const [seqStartFrom, setSeqStartFrom] = useState(1);
  const [seqStarting, setSeqStarting] = useState(false);
  const [seqStartError, setSeqStartError] = useState("");
  const [addEmail, setAddEmail] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addSourceNote, setAddSourceNote] = useState("");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");
  const [addOk, setAddOk] = useState(false);
  const [editPhoneId, setEditPhoneId] = useState(null);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [resendErrors, setResendErrors] = useState({});
  const [reviewsData, setReviewsData] = useState(null);
  const [attribData, setAttribData] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [blNav, setBlNav] = useState(null);
  const [countryData, setCountryData] = useState(null);
  const [attrDeleteTarget, setAttrDeleteTarget] = useState(null);
  const [attrResetTarget, setAttrResetTarget] = useState(null);
  const [attrOverrides, setAttrOverrides] = useState({});
  const [attrEditTarget, setAttrEditTarget] = useState(null);
  const [attrResetText, setAttrResetText] = useState("");
  const [auditRows, setAuditRows] = useState(null);
  const [auditAction, setAuditAction] = useState("all");
  const [auditRecordType, setAuditRecordType] = useState("all");
  const [auditAdmin, setAuditAdmin] = useState("all");
  const [auditFromText, setAuditFromText] = useState("");
  const [auditToText, setAuditToText] = useState("");
  const [auditQ, setAuditQ] = useState("");
  const [weeklyGoal, setWeeklyGoal] = useState(0);
  const [weeklyGoalInput, setWeeklyGoalInput] = useState("");
  const [weeklyGoalSaving, setWeeklyGoalSaving] = useState(false);
  const [weeklyGoalError, setWeeklyGoalError] = useState("");
  const [weeklyGoalEditing, setWeeklyGoalEditing] = useState(false);
  const [cageEarlyEnabled, setCageEarlyEnabled] = useState(false);
  const [cageEarlySaving, setCageEarlySaving] = useState(false);
  const [cageEarlyError, setCageEarlyError] = useState("");
  const [digestSending, setDigestSending] = useState(false);
  const [digestMsg, setDigestMsg] = useState("");
  const [pagesOrder, setPagesOrder] = useState(null);
  const [orderSaved, setOrderSaved] = useState(false);
  const [pageDragIdx, setPageDragIdx] = useState(null);
  const [pageDragOverIdx, setPageDragOverIdx] = useState(null);
  const [pageDragArmed, setPageDragArmed] = useState(null);

  useEffect(() => {
    const target = tab === "tracking" ? "/admin/tracking" : "/admin";
    if (window.location.pathname.replace(/\/+$/, "") !== target) {
      window.history.replaceState(null, "", target + window.location.search);
    }
  }, [tab]);

  const loadData = async (password) => {
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Password": password };
      const [c, l, r, bl] = await Promise.all([
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/redirects`, { headers }),
        axios.get(`${API}/admin/book-links`, { headers }),
      ]);
      setContacts(c.data);
      setLeads(l.data);
      setRedirects(r.data);
      setBookLinks(bl.data);
      setAuthed(true);
      axios.get(`${API}/admin/meetings`, { headers }).then((mt) => setMeetings(mt.data)).catch(() => setMeetings([]));
      axios.get(`${API}/admin/notes`, { headers }).then((nt) => setNotes(nt.data)).catch(() => setNotes([]));
      sessionStorage.setItem(STORAGE_KEY, password);
      markInternalAdmin();
      try {
        const sess = await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
        setCsrf(sess.data.csrf_token);
        setPreviewEmailLinks(!!sess.data.preview_email_links);
      } catch {
        setCsrf("");
      }
    } catch (e) {
      if (e.response?.status === 401) {
        setError("Wrong password.");
        sessionStorage.removeItem(STORAGE_KEY);
        setAuthed(false);
      } else {
        setError("Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem(STORAGE_KEY);
    if (saved) loadData(saved);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 200);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!authed) return;
    const headers = { "X-Admin-Password": sessionStorage.getItem(STORAGE_KEY) || pw };
    if (tab === "audit") {
      axios.get(`${API}/audit-log`, { headers }).then((r) => setAuditRows(r.data)).catch(() => setAuditRows([]));
    }
    if (tab === "dashboard") {
      axios.get(`${API}/admin/reviews`, { withCredentials: true }).then((r) => setReviewsData(r.data)).catch(() => setReviewsData([]));
      axios.get(`${API}/admin/settings`, { withCredentials: true })
        .then((r) => {
          const g = Number(r.data?.weekly_goal || 0);
          setWeeklyGoal(g);
          setWeeklyGoalInput(g ? String(g) : "");
          setCageEarlyEnabled(Boolean(r.data?.cage_early_access_enabled));
        })
        .catch(() => {});
      const invalid = dashFrom && dashTo && dashFrom > dashTo;
      const params = invalid
        ? {}
        : { ...(dashFrom ? { date_from: dashFrom } : {}), ...(dashTo ? { date_to: dashTo } : {}) };
      axios.get(`${API}/admin/attribution`, { headers, params }).then((r) => setAttribData(r.data)).catch(() => setAttribData({ visits_by_source: {}, visits_by_campaign: {} }));
      axios.get(`${API}/admin/attribution/overrides`, { headers }).then((r) => setAttrOverrides(r.data || {})).catch(() => setAttrOverrides({}));
      axios.get(`${API}/admin/device-traffic`, { headers, params }).then((r) => setDeviceData(r.data)).catch(() => setDeviceData(null));
      axios.get(`${API}/admin/bl-nav`, { withCredentials: true }).then((r) => setBlNav(r.data)).catch(() => setBlNav(null));
      axios.get(`${API}/admin/country-traffic`, { headers, params }).then((r) => setCountryData(r.data)).catch(() => setCountryData(null));
    }
    if (tab === "pages") {
      axios.get(`${API}/admin/settings`, { withCredentials: true })
        .then((r) => setPagesOrder(Array.isArray(r.data?.pages_order) ? r.data.pages_order : []))
        .catch(() => {});
    }
  }, [tab, authed, dashFrom, dashTo]);

  const submit = (e) => {
    e.preventDefault();
    if (!pw) return;
    loadData(pw);
  };

  const logout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    axios.post(`${API}/admin/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuthed(false);
    setPw("");
    setCsrf("");
    setContacts([]);
    setLeads([]);
    setMeetings([]);
    setNotes([]);
    setRedirects([]);
    setDashFrom("");
    setDashTo("");
    setDashPreset("all");
  };

  const saveWeeklyGoal = async () => {
    setWeeklyGoalSaving(true);
    setWeeklyGoalError("");
    const n = Number(weeklyGoalInput);
    if (!Number.isFinite(n) || n < 0 || n > 100000 || !Number.isInteger(n)) {
      setWeeklyGoalError("Enter a whole number between 0 and 100000.");
      setWeeklyGoalSaving(false);
      return false;
    }
    try {
      await axios.patch(
        `${API}/admin/settings`,
        { weekly_goal: n },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setWeeklyGoal(n);
      setWeeklyGoalInput(n ? String(n) : "");
      setWeeklyGoalEditing(false);
      return true;
    } catch (e) {
      setWeeklyGoalError("Save failed. Log out and back in, then try again.");
      return false;
    } finally {
      setWeeklyGoalSaving(false);
    }
  };

  const toggleCageEarly = async (next) => {
    setCageEarlySaving(true);
    setCageEarlyError("");
    try {
      await axios.patch(
        `${API}/admin/settings`,
        { cage_early_access_enabled: next },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setCageEarlyEnabled(next);
    } catch (e) {
      setCageEarlyError("Save failed. Log out and back in, then try again.");
    } finally {
      setCageEarlySaving(false);
    }
  };

  const orderedPages = useMemo(() => {
    const saved = Array.isArray(pagesOrder) ? pagesOrder.filter((p) => SITE_PAGES.includes(p)) : [];
    const rest = SITE_PAGES.filter((p) => !saved.includes(p));
    return [...saved, ...rest];
  }, [pagesOrder]);

  const savePagesOrder = async (next) => {
    setPagesOrder(next);
    try {
      await axios.patch(
        `${API}/admin/settings`,
        { pages_order: next },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 2500);
    } catch (e) {}
  };

  const movePage = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= orderedPages.length) return;
    const next = [...orderedPages];
    [next[idx], next[to]] = [next[to], next[idx]];
    savePagesOrder(next);
  };

  const dropPage = (targetIdx) => {
    if (pageDragIdx === null || pageDragIdx === targetIdx) {
      setPageDragIdx(null);
      setPageDragOverIdx(null);
      return;
    }
    const next = [...orderedPages];
    const [moved] = next.splice(pageDragIdx, 1);
    next.splice(targetIdx, 0, moved);
    setPageDragIdx(null);
    setPageDragOverIdx(null);
    savePagesOrder(next);
  };

  const confirmDeleteLead = async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    setError("");
    try {
      await axios.delete(`${API}/leads/${target.id}`, {
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      setLeads((prev) => prev.filter((l) => l.id !== target.id));
      setBookLinks((prev) => prev.filter((b) => b.id !== target.id));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[target.id]; return n; });
      if (expandedLead === target.id) setExpandedLead(null);
    } catch (e) {
      setError("Delete failed. Log out and back in, then try again.");
    }
  };

  const confirmDeleteContact = async () => {
    const target = deleteContactTarget;
    setDeleteContactTarget(null);
    setError("");
    try {
      await axios.delete(`${API}/contact/${target.id}`, {
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      setContacts((prev) => prev.filter((c) => c.id !== target.id));
    } catch (e) {
      setError("Delete failed. Log out and back in, then try again.");
    }
  };

  const refreshAttribution = () => {
    const headers = { "X-Admin-Password": sessionStorage.getItem(STORAGE_KEY) || pw };
    axios.get(`${API}/admin/attribution`, { headers }).then((r) => setAttribData(r.data)).catch(() => {});
    axios.get(`${API}/admin/attribution/overrides`, { headers }).then((r) => setAttrOverrides(r.data || {})).catch(() => {});
  };

  const saveAttrOverride = async (vals) => {
    setError("");
    const offset_visits = vals.visits - vals.rawVisits;
    const offset_leads = (vals.leads ?? 0) - (vals.rawLeads ?? 0);
    const offset_downloads = (vals.downloads ?? 0) - (vals.rawDownloads ?? 0);
    const offset_unique = (vals.unique ?? 0) - (vals.rawUnique ?? 0);
    try {
      await axios.put(
        `${API}/admin/attribution/overrides`,
        { type: vals.type, key: vals.key, offset_visits, offset_leads, offset_downloads, offset_unique },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setAttrOverrides((prev) => ({
        ...prev,
        [`${vals.type}:${vals.key}`]: { offset_visits, offset_leads, offset_downloads, offset_unique },
      }));
      setAttrEditTarget(null);
    } catch (e) {
      setError("Saving the manual values failed. Log out and back in, then try again.");
    }
  };

  const resetAttrOverride = async (vals) => {
    setError("");
    try {
      await axios.delete(`${API}/admin/attribution/overrides/${encodeURIComponent(vals.type)}/${encodeURIComponent(vals.key)}`, {
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      setAttrOverrides((prev) => {
        const n = { ...prev };
        delete n[`${vals.type}:${vals.key}`];
        return n;
      });
      setAttrEditTarget(null);
    } catch (e) {
      setError("Reverting to automatic values failed. Log out and back in, then try again.");
    }
  };

  const openSeqStart = (l) => {
    setSeqStartError("");
    setSeqStartFrom(nyDayDiffFromNow(l.book_sent_at) > 3 ? 2 : 1);
    setSeqStartTarget(l);
  };

  const startSequence = async () => {
    if (!seqStartTarget) return;
    setSeqStarting(true);
    setSeqStartError("");
    try {
      const res = await axios.post(
        `${API}/leads/${seqStartTarget.id}/sequence/start`,
        { start_from: seqStartFrom },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setLeads((prev) => prev.map((l) => (l.id === seqStartTarget.id
        ? { ...l, sequence: res.data.sequence, sequence_block: res.data.sequence_block }
        : l)));
      setSeqStartTarget(null);
    } catch (e) {
      setSeqStartError(adminErrorDetail(e, "Starting the sequence failed. Log out and back in, then try again."));
    } finally {
      setSeqStarting(false);
    }
  };

  const reloadLeads = async () => {
    const pw = sessionStorage.getItem(STORAGE_KEY);
    if (!pw) return;
    const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Password": pw } });
    setLeads(res.data);
  };

  const reloadMeetings = async () => {
    const pw = sessionStorage.getItem(STORAGE_KEY);
    if (!pw) return;
    const res = await axios.get(`${API}/admin/meetings`, { headers: { "X-Admin-Password": pw } });
    setMeetings(res.data);
  };

  const reloadNotes = async () => {
    const pw = sessionStorage.getItem(STORAGE_KEY);
    if (!pw) return;
    const res = await axios.get(`${API}/admin/notes`, { headers: { "X-Admin-Password": pw } });
    setNotes(res.data);
  };

  const addLeadManual = async () => {
    const email = addEmail.trim();
    if (!email) { setAddError("Email is required."); return; }
    setAdding(true);
    setAddError("");
    setAddOk(false);
    try {
      await axios.post(
        `${API}/leads/manual`,
        { email, phone: addPhone.trim(), source_note: addSourceNote.trim() },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setAddEmail("");
      setAddPhone("");
      setAddSourceNote("");
      setAddOk(true);
      await reloadLeads();
      setTimeout(() => setAddOk(false), 3000);
    } catch (e) {
      setAddError(adminErrorDetail(e, "Adding the lead failed. Log out and back in, then try again."));
    } finally {
      setAdding(false);
    }
  };

  const startEditPhone = (l) => {
    setEditPhoneId(l.id);
    setPhoneDraft(l.phone || "");
  };

  const saveLeadPhone = async (l) => {
    setSavingPhone(true);
    try {
      const res = await axios.put(
        `${API}/leads/${l.id}/phone`,
        { phone: phoneDraft.trim() },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, phone: res.data.phone } : x)));
      setEditPhoneId(null);
    } catch (e) {
      /* keep the editor open on failure */
    } finally {
      setSavingPhone(false);
    }
  };

  const stripTouch = (l, prefix) => {    const n = { ...l };
    ["source", "medium", "campaign", "content", "at"].forEach((f) => delete n[`${prefix}_touch_${f}`]);
    return n;
  };

  const confirmAttrDelete = async () => {
    const t = attrDeleteTarget;
    setAttrDeleteTarget(null);
    setError("");
    try {
      if (t.type === "channel") {
        await axios.delete(`${API}/admin/attribution/channel/${encodeURIComponent(t.key)}`, {
          headers: { "X-CSRF-Token": csrf },
          withCredentials: true,
        });
        const src = t.key === "__direct__" ? "direct" : t.key;
        setLeads((prev) => prev.map((l) => {
          let n = l;
          if (n.first_touch_source === src) n = stripTouch(n, "first");
          if (n.last_touch_source === src) n = stripTouch(n, "last");
          return n;
        }));
      } else {
        await axios.delete(`${API}/admin/attribution/campaign/${encodeURIComponent(t.key)}`, {
          headers: { "X-CSRF-Token": csrf },
          withCredentials: true,
        });
        setLeads((prev) => prev.map((l) => ({
          ...l,
          first_touch_campaign: l.first_touch_campaign === t.key ? null : l.first_touch_campaign,
          last_touch_campaign: l.last_touch_campaign === t.key ? null : l.last_touch_campaign,
        })));
      }
      refreshAttribution();
    } catch (e) {
      setError("Attribution delete failed. Log out and back in, then try again.");
    }
  };

  const confirmAttrReset = async () => {
    setAttrResetTarget(null);
    setAttrResetText("");
    setError("");
    try {
      await axios.post(
        `${API}/admin/attribution/reset`,
        { confirm: "RESET" },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setLeads((prev) => prev.map((l) => stripTouch(stripTouch(l, "first"), "last")));
      setAttribData({ visits_by_source: {}, visits_by_campaign: {} });
      refreshAttribution();
    } catch (e) {
      setError("Attribution reset failed. Log out and back in, then try again.");
    }
  };

  const handleSendDigestNow = async () => {
    setDigestSending(true);
    setDigestMsg("");
    setError("");
    try {
      await axios.post(
        `${API}/admin/send-weekly-digest`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setDigestMsg("Weekly digest sent.");
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || "";
      if (e && e.response && e.response.status === 503) {
        setError(detail || "Email sending is not configured or the send failed.");
      } else {
        setError(adminErrorDetail(e, "Sending the weekly digest failed. Log out and back in, then try again."));
      }
    } finally {
      setDigestSending(false);
    }
  };

  const confirmMarkBookSent = async () => {
    const target = markSentTarget;
    setMarkSentTarget(null);
    setError("");
    try {
      const res = await axios.post(
        `${API}/leads/${target.id}/mark-book-sent`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      const updated = res.data.lead;
      setLeads((prev) => prev.map((l) => (l.id === target.id ? { ...l, ...updated } : l)));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[target.id]; return n; });
    } catch (e) {
      setError("Recording manual send failed. Log out and back in, then try again.");
    }
  };

  const toggleHistory = async (l) => {
    if (expandedLead === l.id) {
      setExpandedLead(null);
      return;
    }
    setExpandedLead(l.id);
    if (!leadHistory[l.id]) {
      try {
        const headers = { "X-Admin-Password": sessionStorage.getItem(STORAGE_KEY) || pw };
        const res = await axios.get(`${API}/leads/${l.id}/history`, { headers });
        setLeadHistory((prev) => ({ ...prev, [l.id]: res.data.events }));
      } catch {
        setLeadHistory((prev) => ({ ...prev, [l.id]: [] }));
      }
    }
  };

  const confirmResendBook = async () => {
    const target = resendTarget;
    setResendTarget(null);
    try {
      const res = await axios.post(
        `${API}/leads/${target.id}/resend-book`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      if (res.data.lead) setLeads((prev) => prev.map((l) => (l.id === target.id ? { ...l, ...res.data.lead } : l)));
      setResendErrors((prev) => ({ ...prev, [target.id]: res.data.ok ? "" : (res.data.error || "Send failed.") }));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[target.id]; return n; });
    } catch (e) {
      setResendErrors((prev) => ({
        ...prev,
        [target.id]: adminErrorDetail(e, "Resend failed. Log out and back in, then try again."),
      }));
    }
  };

  const changeFollowup = async (l, status) => {
    try {
      const res = await axios.patch(
        `${API}/leads/${l.id}/followup-status`,
        { status },
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, ...res.data.lead } : x)));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[l.id]; return n; });
    } catch {
      setError("Follow-up update failed. Log out and back in, then try again.");
    }
  };

  const revokeToken = async (l) => {
    setRevokeConfirm(null);
    try {
      const res = await axios.post(
        `${API}/leads/${l.id}/revoke-token`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setLeads((prev) => prev.map((x) => (x.id === l.id ? { ...x, ...res.data.lead } : x)));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[l.id]; return n; });
    } catch {
      setError("Revoke failed. Log out and back in, then try again.");
    }
  };

  const markReviewed = async (b) => {
    try {
      await axios.post(
        `${API}/leads/${b.id}/mark-reviewed`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setBookLinks((prev) => prev.filter((x) => x.id !== b.id));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[b.id]; return n; });
    } catch {
      setError("Mark as reviewed failed. Log out and back in, then try again.");
    }
  };

  const revokeBookLink = async (b) => {
    setBlRevokeConfirm(null);
    try {
      const res = await axios.post(
        `${API}/leads/${b.id}/revoke-token`,
        {},
        { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
      );
      setBookLinks((prev) => prev.filter((x) => x.id !== b.id));
      setLeads((prev) => prev.map((x) => (x.id === b.id ? { ...x, ...res.data.lead } : x)));
      setLeadHistory((prev) => { const n = { ...prev }; delete n[b.id]; return n; });
    } catch {
      setError("Revoke failed. Log out and back in, then try again.");
    }
  };

  // ----- Redirects: client-side validation, save, delete -----
  const campaignMeta = (dest) => {
    const q = (dest || "").split("?")[1];
    if (!q) return null;
    const params = new URLSearchParams(q);
    const source = params.get("utm_source");
    return source ? { source, campaign: params.get("utm_campaign") || "" } : null;
  };
  const redirectDestinationPath = (dest) => {
    const s = (dest || "").trim();
    if (!s || /^https?:\/\//i.test(s)) return null;
    return s.split("?", 1)[0].toLowerCase();
  };
  const redirectChainWarning = () => {
    const destPath = redirectDestinationPath(redirectForm.destination);
    if (!destPath) return "";
    const chained = redirects.find(
      (r) => r.source.toLowerCase() === destPath && r.id !== redirectForm.id,
    );
    return chained
      ? `Warning: destination points to another redirect (${chained.source} → ${chained.destination}). This creates a chain.`
      : "";
  };
  const submitRedirect = async (e) => {
    e.preventDefault();
    setRedirectFormError("");
    const source = redirectForm.source.trim().toLowerCase();
    const destination = redirectForm.destination.trim();
    if (!source.startsWith("/")) return setRedirectFormError("Source path must start with '/'.");
    if (/\s/.test(source)) return setRedirectFormError("Source cannot contain whitespace.");
    if (!destination) return setRedirectFormError("Destination is required.");
    if (!(destination.startsWith("/") || /^https?:\/\//i.test(destination)))
      return setRedirectFormError("Destination must be a path starting with '/' or a full http(s) URL.");
    if (isReservedPath(source))
      return setRedirectFormError(`${source} is an existing site page — a redirect here would be unreachable.`);
    const dup = redirects.find((r) => r.source.toLowerCase() === source && r.id !== redirectForm.id);
    if (dup) return setRedirectFormError(`A redirect already exists for ${source}. Edit it instead of adding a new one.`);
    try {
      const body = { source, destination, type: Number(redirectForm.type) || 301 };
      const cfg = { headers: { "X-CSRF-Token": csrf }, withCredentials: true };
      if (redirectForm.id) {
        const res = await axios.put(`${API}/redirects/${redirectForm.id}`, body, cfg);
        setRedirects((prev) => prev.map((r) => (r.id === redirectForm.id ? res.data : r)));
      } else {
        const res = await axios.post(`${API}/redirects`, body, cfg);
        setRedirects((prev) => [res.data, ...prev]);
      }
      setRedirectForm(EMPTY_REDIRECT_FORM);
    } catch (err) {
      setRedirectFormError(adminErrorDetail(err, "Save failed. Log out and back in, then try again."));
    }
  };
  const editRedirect = (r) => {
    setRedirectFormError("");
    setRedirectForm({ id: r.id, source: r.source, destination: r.destination, type: r.type });
  };
  const cancelRedirectEdit = () => {
    setRedirectFormError("");
    setRedirectForm(EMPTY_REDIRECT_FORM);
  };
  const confirmDeleteRedirect = async () => {
    const target = deleteRedirectTarget;
    setDeleteRedirectTarget(null);
    try {
      await axios.delete(`${API}/redirects/${target.id}`, {
        headers: { "X-CSRF-Token": csrf },
        withCredentials: true,
      });
      setRedirects((prev) => prev.filter((r) => r.id !== target.id));
      if (redirectForm.id === target.id) setRedirectForm(EMPTY_REDIRECT_FORM);
    } catch {
      setError("Delete failed. Log out and back in, then try again.");
    }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });

  // CSV export: RFC-4180 escaping, UTF-8 BOM for Excel compatibility.
  // Downloads the currently filtered set. Filters act as the selection.
  const exportCsv = (filename, headers, rows) => {
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(","), ...rows.map((r) => r.map(esc).join(","))];
    const body = "\uFEFF" + lines.join("\r\n");
    const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };
  const todayStr = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const auditExport = (recordType, count) => {
    axios.post(
      `${API}/audit/csv-export`,
      { record_type: recordType, count },
      { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
    ).catch(() => {});
  };
  const exportContactsCsv = () => {
    exportCsv(
      `contacts-${todayStr()}.csv`,
      ["date", "name", "email", "message"],
      filteredContacts.map((c) => [c.created_at || "", c.name || "", c.email || "", c.message || ""]),
    );
    auditExport("contacts", filteredContacts.length);
  };
  const exportLeadsCsv = () => {
    exportCsv(
      `leads-${todayStr()}.csv`,
      ["date", "email", "phone", "tags", "sent_status", "book_sent_at", "downloaded_at"],
      visibleLeads.map((l) => [
        l.created_at || "",
        l.email || "",
        l.phone || "",
        (l.sources && l.sources.length ? l.sources : [l.source]).filter(Boolean).join("; "),
        l.book_sent_at ? "sent" : "not_sent",
        l.book_sent_at || "",
        l.downloaded_at || "",
      ]),
    );
    auditExport("leads", visibleLeads.length);
  };

  const filteredContacts = contacts.filter((c) => {
    const s = q.toLowerCase();
    const cs = contactSearch.toLowerCase().trim();
    const matchGlobal = !s || c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || c.message.toLowerCase().includes(s);
    const matchLocal = !cs || c.name.toLowerCase().includes(cs) || c.email.toLowerCase().includes(cs) || c.message.toLowerCase().includes(cs);
    return matchGlobal && matchLocal;
  });
  const localDayStr = (iso) => {
    const d = new Date(iso);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const daysAgoStr = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    const p = (x) => String(x).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  };
  const leadDateBounds = (() => {
    if (leadFrom || leadTo) return { from: leadFrom || null, to: leadTo || null };
    if (leadDatePreset === "today") { const t = todayStr(); return { from: t, to: t }; }
    if (leadDatePreset === "7d") return { from: daysAgoStr(6), to: todayStr() };
    if (leadDatePreset === "30d") return { from: daysAgoStr(29), to: todayStr() };
    return { from: null, to: null };
  })();
  const matchesLeadDate = (l) => {
    const { from, to } = leadDateBounds;
    if (!from && !to) return true;
    const day = localDayStr(l.created_at);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  };
  const datePresetActive = (key) => !leadFrom && !leadTo && leadDatePreset === key;
  const parseDmy = (s) => {
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s.trim());
    if (!m) return "";
    const d = +m[1], mo = +m[2], y = +m[3];
    const dt = new Date(y, mo - 1, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${y}-${p(mo)}-${p(d)}`;
  };
  const selectDatePreset = (key) => {
    setLeadDatePreset(key);
    setLeadFrom("");
    setLeadTo("");
    setLeadFromText("");
    setLeadToText("");
  };
  const onCustomDate = (which, text) => {
    const iso = parseDmy(text);
    if (which === "from") {
      setLeadFromText(text);
      setLeadFrom(iso);
    } else {
      setLeadToText(text);
      setLeadTo(iso);
    }
    const fromText = which === "from" ? text : leadFromText;
    const toText = which === "to" ? text : leadToText;
    if (!fromText.trim() && !toText.trim()) setLeadDatePreset("all");
  };
  const filteredLeads = leads.filter((l) => {
    const email = l.email.toLowerCase();
    const codeMatch = (query) => {
      const code = query.trim().toLowerCase().replace(/^tbt-/, "");
      return code.length >= 4 && /^[0-9a-f-]+$/.test(code) && l.id.toLowerCase().startsWith(code);
    };
    const leadDigits = String(l.phone || "").replace(/\D/g, "");
    const phoneMatch = (query) => {
      const qd = String(query || "").replace(/\D/g, "");
      return qd.length > 0 && leadDigits.includes(qd);
    };
    if (q && !email.includes(q.toLowerCase()) && !codeMatch(q) && !phoneMatch(q)) return false;
    if (leadQ && !email.includes(leadQ.toLowerCase()) && !codeMatch(leadQ) && !phoneMatch(leadQ)) return false;
    return matchesLeadDate(l);
  });

  const leadTags = (l) => {
    const raw = l.sources && l.sources.length ? l.sources : [l.source];
    return raw.filter(Boolean).map((s) => String(s).toLowerCase());
  };
  const hasTag = (l, tag) => leadTags(l).includes(tag);
  const matchesSource = (l, src) => src === "all" || hasTag(l, src);
  const matchesSent = (l, key) => {
    if (key === "sent") return !!l.book_sent_at;
    if (key === "notsent") return !l.book_sent_at;
    return true;
  };
  const matchesDownload = (l, key) => {
    if (key === "downloaded") return !!l.downloaded_at;
    if (key === "notdownloaded") return !l.downloaded_at;
    return true;
  };
  const matchesUnsub = (l, key) => {
    if (key === "subscribed") return !l.unsubscribed;
    if (key === "unsubscribed") return !!l.unsubscribed;
    return true;
  };
  const matchesVia = (l, key) => {
    if (key === "all") return true;
    if (!l.downloaded_at) return false;
    return (l.download_via || "unknown") === key;
  };

  const LEAD_SENT_TABS = [
    { key: "all", label: "All" },
    { key: "sent", label: "Book sent" },
    { key: "notsent", label: "Not sent" },
  ];
  const LEAD_DOWNLOAD_TABS = [
    { key: "all", label: "All" },
    { key: "downloaded", label: "Downloaded" },
    { key: "notdownloaded", label: "Not downloaded" },
  ];
  const LEAD_UNSUB_TABS = [
    { key: "all", label: "All" },
    { key: "subscribed", label: "Subscribed" },
    { key: "unsubscribed", label: "Unsubscribed" },
  ];
  const LEAD_VIA_TABS = [
    { key: "all", label: "All routes" },
    { key: "thankyou", label: "Thank-you page" },
    { key: "email", label: "Email link" },
    { key: "ebook", label: "Ebook page" },
    { key: "unknown", label: "Unknown" },
  ];
  const LEAD_SOURCES = [
    { key: "all", label: "All" },
    { key: "landing", label: "Landing" },
    { key: "ebook", label: "Ebook" },
    { key: "book", label: "Book" },
    { key: "review", label: "Review" },
    { key: "manual", label: "Manual" },
    { key: "cage_system", label: "Cage System" },
  ];
  const LEAD_DATE_PRESETS = [
    { key: "all", label: "All" },
    { key: "today", label: "Today" },
    { key: "7d", label: "Last 7 days" },
    { key: "30d", label: "Last 30 days" },
  ];
  // Counts on each row reflect the OTHER two rows' active filters (AND combination)
  const sentGroups = Object.fromEntries(
    LEAD_SENT_TABS.map((t) => [
      t.key,
      filteredLeads.filter(
        (l) => matchesSent(l, t.key) && matchesDownload(l, leadDownload) && matchesSource(l, leadSource) && matchesUnsub(l, leadUnsub) && matchesVia(l, leadVia),
      ),
    ]),
  );
  const downloadGroups = Object.fromEntries(
    LEAD_DOWNLOAD_TABS.map((t) => [
      t.key,
      filteredLeads.filter(
        (l) => matchesSent(l, leadSent) && matchesDownload(l, t.key) && matchesSource(l, leadSource) && matchesUnsub(l, leadUnsub) && matchesVia(l, leadVia),
      ),
    ]),
  );
  const unsubGroups = Object.fromEntries(
    LEAD_UNSUB_TABS.map((t) => [
      t.key,
      filteredLeads.filter(
        (l) => matchesSent(l, leadSent) && matchesDownload(l, leadDownload) && matchesSource(l, leadSource) && matchesUnsub(l, t.key) && matchesVia(l, leadVia),
      ),
    ]),
  );
  const sourceGroups = Object.fromEntries(
    LEAD_SOURCES.map((s) => [
      s.key,
      filteredLeads.filter(
        (l) => matchesSent(l, leadSent) && matchesDownload(l, leadDownload) && matchesSource(l, s.key) && matchesUnsub(l, leadUnsub) && matchesVia(l, leadVia),
      ),
    ]),
  );
  const viaGroups = Object.fromEntries(
    LEAD_VIA_TABS.map((t) => [
      t.key,
      filteredLeads.filter(
        (l) => matchesSent(l, leadSent) && matchesDownload(l, leadDownload) && matchesSource(l, leadSource) && matchesUnsub(l, leadUnsub) && matchesVia(l, t.key),
      ),
    ]),
  );
  const visibleLeads = filteredLeads.filter(
    (l) => matchesSent(l, leadSent) && matchesDownload(l, leadDownload) && matchesSource(l, leadSource) && matchesUnsub(l, leadUnsub) && matchesVia(l, leadVia),
  );

  // ----- Audit log filtering (client-side) -----
  const kvPairs = (o) =>
    o && Object.keys(o).length
      ? Object.entries(o).map(([k, v]) => `${k}: ${v === null || v === undefined ? "—" : String(v)}`).join(", ")
      : "";
  const auditChange = (a) => {
    const parts = [];
    if (a.before || a.after) parts.push(`${kvPairs(a.before) || "—"} → ${kvPairs(a.after) || "—"}`);
    if (a.detail) parts.push(kvPairs(a.detail));
    return parts.filter(Boolean).join(" · ");
  };
  const auditOptions = (field) =>
    Array.from(new Set((auditRows || []).map((a) => a[field]).filter(Boolean))).sort();
  const auditFromIso = parseDmy(auditFromText);
  const auditToIso = parseDmy(auditToText);
  const filteredAudit = (auditRows || []).filter((a) => {
    if (auditAction !== "all" && a.action !== auditAction) return false;
    if (auditRecordType !== "all" && a.record_type !== auditRecordType) return false;
    if (auditAdmin !== "all" && a.admin !== auditAdmin) return false;
    if (auditFromIso || auditToIso) {
      const day = localDayStr(a.created_at);
      if (auditFromIso && day < auditFromIso) return false;
      if (auditToIso && day > auditToIso) return false;
    }
    const s = auditQ.trim().toLowerCase();
    if (s) {
      const hay = `${a.record_id || ""} ${kvPairs(a.detail)} ${kvPairs(a.before)} ${kvPairs(a.after)}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-paper px-6" data-testid="admin-login">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="label-mono text-muted">Admin</div>
          <h1 className="font-display text-4xl md:text-5xl mt-6 mb-10 leading-tight">Enter password.</h1>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            placeholder="Password"
            className="w-full bg-transparent border-b border-ink py-3 text-lg outline-none"
            data-testid="admin-password-input"
            autoFocus
          />
          {error && <p className="mt-4 text-sm text-red-600" data-testid="admin-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="mt-8 w-full bg-ink text-white py-4 label-mono hover:bg-leaf transition-colors disabled:opacity-60"
            data-testid="admin-submit"
          >
            {loading ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-dashboard">
      <button
        onClick={() => window.scrollTo({ top: scrolled ? 0 : document.documentElement.scrollHeight, behavior: "smooth" })}
        className="fixed bottom-6 right-6 z-40 w-11 h-11 flex items-center justify-center bg-ink text-white shadow-lg hover:bg-leaf transition-colors"
        data-testid="admin-scroll-jump"
        aria-label={scrolled ? "Jump to top" : "Jump to bottom"}
        title={scrolled ? "Jump to top" : "Jump to bottom"}
      >
        {scrolled ? <ArrowUp size={20} /> : <ArrowDown size={20} />}
      </button>
      {previewEmailLinks && (
        <div className="bg-ink text-white px-6 py-2 label-mono text-center" data-testid="admin-preview-domain-banner">
          Email links point at the preview domain
        </div>
      )}
      <header className="border-b border-hairline sticky top-0 z-10 backdrop-blur-md bg-white/85">
        <div className="max-w-screen-xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="font-display text-lg" style={{ fontWeight: 800 }}>Admin</span>
            <div className="hidden md:flex items-center gap-1 border border-hairline p-1">
              <button
                onClick={() => setTab("contacts")}
                data-testid="tab-contacts"
                className={`label-mono px-4 py-2 transition-colors ${tab === "contacts" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Contacts ({contacts.length})
              </button>
              <button
                onClick={() => setTab("meetings")}
                data-testid="tab-meetings"
                className={`label-mono px-4 py-2 transition-colors ${tab === "meetings" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Meetings ({meetings.filter((m) => m.is_upcoming).length})
              </button>
              <button
                onClick={() => setTab("leads")}
                data-testid="tab-leads"
                className={`label-mono px-4 py-2 transition-colors ${tab === "leads" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Leads ({leads.length})
              </button>
              <button
                onClick={() => setTab("pages")}
                data-testid="tab-pages"
                className={`label-mono px-4 py-2 transition-colors ${tab === "pages" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Pages ({SITE_PAGES.length})
              </button>
              <button
                onClick={() => setTab("dashboard")}
                data-testid="tab-dashboard"
                className={`label-mono px-4 py-2 transition-colors ${tab === "dashboard" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Dashboard
              </button>
              <button
                onClick={() => setTab("tracking")}
                data-testid="tab-tracking"
                className={`label-mono px-4 py-2 transition-colors ${tab === "tracking" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Distribution
              </button>
              <button
                onClick={() => setTab("sequence")}
                data-testid="tab-sequence"
                className={`label-mono px-4 py-2 transition-colors ${tab === "sequence" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Sequence
              </button>
              <button
                onClick={() => setTab("booklinks")}
                data-testid="tab-booklinks"
                className={`label-mono px-4 py-2 transition-colors ${tab === "booklinks" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Book Links ({bookLinks.length})
              </button>
              <button
                onClick={() => setTab("audit")}
                data-testid="tab-audit"
                className={`label-mono px-4 py-2 transition-colors ${tab === "audit" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Audit Log
              </button>
              <button
                onClick={() => setTab("notes")}
                data-testid="tab-notes"
                className={`label-mono px-4 py-2 transition-colors ${tab === "notes" ? "bg-ink text-white" : "text-ink hover:bg-surface"}`}
              >
                Notes ({notes.length})
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/admin/cagesystem"
              target="_blank"
              rel="noopener noreferrer"
              className="label-mono font-bold text-ink border border-ink px-2.5 py-1 hover:bg-ink hover:text-white transition-colors"
              data-testid="admin-cagesystem-link"
            >
              Cage System ↗
            </a>
            <a
              href="/admin/reviews"
              target="_blank"
              rel="noopener noreferrer"
              className="label-mono font-bold text-ink border border-ink px-2.5 py-1 hover:bg-ink hover:text-white transition-colors"
              data-testid="admin-reviews-link"
            >
              Reviews ↗
            </a>
            <a
              href="/admin/broadcasts"
              target="_blank"
              rel="noopener noreferrer"
              className="label-mono font-bold text-ink border border-ink px-2.5 py-1 hover:bg-ink hover:text-white transition-colors"
              data-testid="admin-broadcasts-link"
            >
              Broadcasts ↗
            </a>
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-36 md:w-48"
              data-testid="admin-search"
            />
            <button onClick={logout} className="label-mono text-muted hover:text-ink transition-colors" data-testid="admin-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-screen-xl mx-auto px-6 md:px-12 py-12 md:py-16">
        {tab === "tracking" && <AdminTracking embedded parentCsrf={csrf} />}
        {tab !== "tracking" && (
        <>
        {tab === "contacts" ? (
          <div data-testid="table-contacts">
            <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
              <h2 className="font-display text-3xl md:text-4xl">Contact submissions</h2>
              <button
                onClick={exportContactsCsv}
                disabled={filteredContacts.length === 0}
                className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                data-testid="contacts-export-csv"
              >
                Export CSV ({filteredContacts.length})
              </button>
            </div>
            {error && (
              <p className="mb-6 text-sm text-red-600" data-testid="contacts-action-error">{error}</p>
            )}
            <input
              type="text"
              value={contactSearch}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search name, email or message…"
              className="mb-6 w-full bg-transparent border-b border-hairline focus:border-ink py-2 text-sm outline-none"
              data-testid="contacts-search"
            />
            {contacts.length === 0 ? (
              <p className="text-muted">No contact submissions yet.</p>
            ) : filteredContacts.length === 0 ? (
              <p className="text-muted" data-testid="contacts-no-results">No submissions match your search.</p>
            ) : (
              <div className="border-t border-hairline divide-y divide-hairline">
                {filteredContacts.map((c) => (
                  <div key={c.id} className="py-6 grid grid-cols-12 gap-6 items-start" data-testid={`contact-row-${c.id}`}>
                    <div className="col-span-12 md:col-span-3">
                      <div className="label-mono text-muted">{fmt(c.created_at)}</div>
                      <div className="mt-2 font-display text-lg leading-tight">{c.name}</div>
                      <a href={`mailto:${c.email}`} className="text-sm text-ink hover:text-leaf transition-colors">{c.email}</a>
                    </div>
                    <div className="col-span-12 md:col-span-9">
                      <p className="text-ink whitespace-pre-wrap leading-relaxed">{c.message}</p>
                      <div className="mt-3 flex gap-4">
                        <a href={`mailto:${c.email}?subject=Re: your message`} className="label-mono text-ink hover:text-leaf transition-colors">Reply →</a>
                      </div>
                      <div className="mt-3">
                        <button
                          onClick={() => setDeleteContactTarget(c)}
                          className="label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors"
                          data-testid={`contact-delete-${c.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "meetings" ? (
          <AdminMeetings
            csrf={csrf}
            meetings={meetings}
            reloadMeetings={reloadMeetings}
            onOpenLead={(email) => { setTab("leads"); setLeadQ(email); }}
            leads={leads}
          />
        ) : tab === "notes" ? (
          <AdminNotes csrf={csrf} notes={notes} reloadNotes={reloadNotes} />
        ) : tab === "leads" ? (
          <div data-testid="table-leads">
            <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
              <h2 className="font-display text-3xl md:text-4xl">Ebook email leads</h2>
              <button
                onClick={exportLeadsCsv}
                disabled={visibleLeads.length === 0}
                className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                data-testid="leads-export-csv"
              >
                Export CSV ({visibleLeads.length})
              </button>
            </div>
            <div className="mb-8 border border-hairline p-4" data-testid="add-lead-form">
              <div className="label-mono text-muted mb-3">Add lead manually</div>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex flex-col">
                  <label className="label-mono text-muted mb-1" htmlFor="add-lead-email-input">Email *</label>
                  <input
                    id="add-lead-email-input"
                    type="email"
                    value={addEmail}
                    onChange={(e) => setAddEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-64"
                    data-testid="add-lead-email"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="label-mono text-muted mb-1" htmlFor="add-lead-phone-input">Phone</label>
                  <input
                    id="add-lead-phone-input"
                    type="text"
                    value={addPhone}
                    onChange={(e) => setAddPhone(e.target.value)}
                    placeholder="optional"
                    className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-48"
                    data-testid="add-lead-phone"
                  />
                </div>
                <div className="flex flex-col">
                  <label className="label-mono text-muted mb-1" htmlFor="add-lead-note-input">Source note</label>
                  <input
                    id="add-lead-note-input"
                    type="text"
                    value={addSourceNote}
                    onChange={(e) => setAddSourceNote(e.target.value)}
                    placeholder="optional"
                    className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-64"
                    data-testid="add-lead-note"
                  />
                </div>
                <button
                  onClick={addLeadManual}
                  disabled={adding || !addEmail.trim()}
                  className="label-mono border border-hairline text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-ink"
                  data-testid="add-lead-submit"
                >
                  {adding ? "Adding…" : "Add lead"}
                </button>
              </div>
              {addError && <p className="mt-3 text-sm text-red-600" data-testid="add-lead-error">{addError}</p>}
              {addOk && <p className="mt-3 text-sm text-leaf" data-testid="add-lead-ok">Lead added. It is suppressed from the auto-sequence until you start it.</p>}
            </div>
            <div className="mb-6">
              <div className="label-mono text-muted">Owned emails</div>
              <div className="font-display text-4xl md:text-5xl mt-1" data-testid="leads-owned-total">{leads.length}</div>
            </div>
            <div className="relative w-fit mb-3">
              <input
                type="text"
                value={leadQ}
                onChange={(e) => setLeadQ(e.target.value)}
                placeholder="Search email..."
                className="bg-transparent border-b border-hairline focus:border-ink py-1 pr-6 text-sm outline-none w-64"
                data-testid="leads-search"
              />
              {leadQ && (
                <button
                  onClick={() => setLeadQ("")}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-muted hover:text-ink text-sm leading-none px-1"
                  aria-label="Clear search"
                  data-testid="leads-search-clear"
                >
                  ×
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-3 overflow-x-auto">
              {LEAD_SENT_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setLeadSent(t.key)}
                  data-testid={`lead-sent-${t.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadSent === t.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {t.label} ({sentGroups[t.key].length})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-3 overflow-x-auto">
              {LEAD_DOWNLOAD_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setLeadDownload(t.key)}
                  data-testid={`lead-download-${t.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadDownload === t.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {t.label} ({downloadGroups[t.key].length})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-3 overflow-x-auto">
              {LEAD_VIA_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setLeadVia(t.key)}
                  data-testid={`lead-via-${t.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadVia === t.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {t.label} ({viaGroups[t.key].length})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-3 overflow-x-auto">
              {LEAD_UNSUB_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setLeadUnsub(t.key)}
                  data-testid={`lead-unsub-${t.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadUnsub === t.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {t.label} ({unsubGroups[t.key].length})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-3 overflow-x-auto">
              {LEAD_SOURCES.filter((s) => s.key !== "cage_system").map((s) => (
                <button
                  key={s.key}
                  onClick={() => setLeadSource(s.key)}
                  data-testid={`lead-source-${s.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadSource === s.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {s.label} ({sourceGroups[s.key].length})
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1 border border-hairline p-1 w-fit mb-8 overflow-x-auto">
              {LEAD_SOURCES.filter((s) => s.key === "cage_system").map((s) => (
                <button
                  key={s.key}
                  onClick={() => setLeadSource(s.key)}
                  data-testid={`lead-source-${s.key}`}
                  className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                    leadSource === s.key ? "bg-ink text-white" : "text-ink hover:bg-surface"
                  }`}
                >
                  {s.label} ({sourceGroups[s.key].length})
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-8" data-testid="leads-date-row">
              <div className="flex items-center gap-1 border border-hairline p-1 w-fit overflow-x-auto">
                {LEAD_DATE_PRESETS.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => selectDatePreset(t.key)}
                    data-testid={`lead-date-${t.key}`}
                    className={`label-mono px-3 md:px-4 py-2 transition-colors whitespace-nowrap ${
                      datePresetActive(t.key) ? "bg-ink text-white" : "text-ink hover:bg-surface"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 ml-2 md:ml-8">
                <label className="label-mono text-muted" htmlFor="lead-date-from">From</label>
                <input
                  id="lead-date-from"
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/yyyy"
                  value={leadFromText}
                  onChange={(e) => onCustomDate("from", e.target.value)}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-28"
                  data-testid="lead-date-from"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="label-mono text-muted" htmlFor="lead-date-to">To</label>
                <input
                  id="lead-date-to"
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/yyyy"
                  value={leadToText}
                  onChange={(e) => onCustomDate("to", e.target.value)}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-28"
                  data-testid="lead-date-to"
                />
              </div>
            </div>
            {error && (
              <p className="mb-6 text-sm text-red-600" data-testid="leads-action-error">{error}</p>
            )}
            <p className="label-mono text-muted mb-3" data-testid="leads-result-count">
              Showing {visibleLeads.length} of {leads.length}
            </p>
            {visibleLeads.length === 0 ? (
              <p className="text-muted" data-testid="leads-empty">
                No leads match this filter.{" "}
                {(leadSent !== "all" || leadDownload !== "all" || leadSource !== "all" || leadUnsub !== "all" || leadVia !== "all" || leadQ || leadFrom || leadTo || leadDatePreset !== "all") && (
                  <button
                    onClick={() => {
                      setLeadSent("all");
                      setLeadDownload("all");
                      setLeadSource("all");
                      setLeadUnsub("all");
                      setLeadVia("all");
                      setLeadQ("");
                      selectDatePreset("all");
                    }}
                    className="underline underline-offset-4 hover:text-ink"
                    data-testid="leads-clear-filters"
                  >
                    Clear filters
                  </button>
                )}
              </p>
            ) : (
              <div className="border-t border-hairline divide-y divide-hairline">
                {visibleLeads.map((l, i) => (
                  <div
                    key={l.id}
                    className="py-4 px-4"
                    style={i % 2 === 1 ? { backgroundColor: "#F5F3EF" } : undefined}
                    data-testid={`lead-row-${l.id}`}
                  >
                    <div className="grid grid-cols-12 gap-4 md:gap-6 items-baseline">
                      <div className="col-span-6 md:col-span-3 label-mono text-muted">{fmt(l.created_at)}</div>
                      <a href={`mailto:${l.email}`} className="col-span-6 md:col-span-3 text-ink hover:text-leaf transition-colors break-all">
                        {(reviewsData || []).some((rv) => !rv.deleted_at && rv.status === "approved" && (rv.email_lower || rv.email || "").toLowerCase() === (l.email || "").toLowerCase()) ? (
                          <span
                            className="inline-block px-2 py-0.5 text-sm rounded"
                            style={{ backgroundColor: "#EDF3EE", color: "#2F6B3F", border: "1px solid #C9DDD0" }}
                            data-testid={`lead-approved-badge-${l.id}`}
                          >
                            {l.email}
                          </span>
                        ) : (
                          l.email
                        )}
                      </a>
                      <div className="col-span-6 md:col-span-2 label-mono text-muted flex flex-wrap gap-1 items-center" data-testid={`lead-sources-${l.id}`}>
                        {(() => {
                          const srcs = (l.sources && l.sources.length ? l.sources : [l.source]).filter(Boolean);
                          const others = srcs.filter((s) => s !== "MANUAL");
                          return (
                            <>
                              {srcs.includes("MANUAL") && (
                                <span
                                  className="inline-block px-2 py-0.5 rounded"
                                  style={{ backgroundColor: "#F8EEEA", color: "#9E2B20", border: "1px solid #E8D3CE" }}
                                  data-testid={`lead-manual-badge-${l.id}`}
                                >
                                  MANUAL
                                </span>
                              )}
                              {others.length > 0 && <span>{others.join(", ")}</span>}
                            </>
                          );
                        })()}
                      </div>
                      <div className="col-span-6 md:col-span-2 label-mono text-muted" data-testid={`lead-downloaded-${l.id}`}>
                        {l.downloaded_at ? fmt(l.downloaded_at) : "—"}
                      </div>
                      <div className="col-span-12 md:col-span-2 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => setMarkSentTarget(l)}
                          className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
                          data-testid={`lead-mark-sent-${l.id}`}
                        >
                          Record send
                        </button>
                        <button
                          onClick={() => setDeleteTarget(l)}
                          className="label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors"
                          data-testid={`lead-delete-${l.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1">
                      <span className="label-mono text-muted" data-testid={`lead-delivery-${l.id}`}>
                        Book: {DELIVERY_LABELS[dvOf(l)] || dvOf(l)}
                      </span>
                      <span className="label-mono text-muted" data-testid={`lead-followup-${l.id}`}>
                        Follow-up: {FOLLOWUP_LABELS[fuOf(l)] || fuOf(l)}
                      </span>
                      {l.unsubscribed && (
                        <span className="label-mono text-red-600" data-testid={`lead-unsubscribed-${l.id}`}>
                          Unsubscribed
                        </span>
                      )}
                      {(l.download_count || 0) > 25 && (
                        <span className="label-mono text-red-600" data-testid={`lead-high-downloads-${l.id}`}>
                          High download count ({l.download_count})
                        </span>
                      )}
                      {l.token_revoked && (
                        <span className="label-mono text-red-600" data-testid={`lead-token-revoked-${l.id}`}>
                          Link revoked
                        </span>
                      )}
                      <button
                        onClick={() => toggleHistory(l)}
                        className="label-mono text-muted hover:text-ink transition-colors underline underline-offset-4"
                        data-testid={`lead-history-toggle-${l.id}`}
                      >
                        History
                      </button>
                      {(() => {
                        const block = l.sequence_block;
                        if (block) {
                          return (
                            <span className="label-mono text-muted" data-testid={`lead-seq-blocked-${l.id}`}>
                              Sequence: {block}
                            </span>
                          );
                        }
                        const st = l.sequence || {};
                        const status = st.status;
                        if (l.sequence_suppressed && !status && !l.book_sent_at) {
                          return (
                            <span className="label-mono text-muted" data-testid={`lead-seq-needs-book-${l.id}`}>
                              Send the book first, RESEND BOOK (real email) or RECORD SEND (delivery made outside the system), then start the sequence.
                            </span>
                          );
                        }
                        if (SEQ_STARTABLE.includes(status) || (l.sequence_suppressed && !status)) {
                          return (
                            <button
                              onClick={() => openSeqStart(l)}
                              className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-leaf hover:text-white transition-colors"
                              data-testid={`lead-seq-start-${l.id}`}
                            >
                              Start sequence
                            </button>
                          );
                        }
                        if (status === "active") {
                          const n = Math.min(8, (st.last_email || 0) + 1);
                          return (
                            <span className="label-mono text-muted" data-testid={`lead-seq-active-${l.id}`}>
                              In sequence — email {n} of 8
                            </span>
                          );
                        }
                        if (status === "completed") {
                          return (
                            <span className="label-mono text-muted" data-testid={`lead-seq-completed-${l.id}`}>
                              Sequence completed
                            </span>
                          );
                        }
                        if (!status) {
                          return (
                            <span className="label-mono text-muted" data-testid={`lead-seq-pending-${l.id}`}>
                              Enrols on next run
                            </span>
                          );
                        }
                        return (
                          <span className="label-mono text-muted" data-testid={`lead-seq-status-${l.id}`}>
                            Sequence: {status}
                          </span>
                        );
                      })()}
                      {canResend(l) && (
                        <button
                          onClick={() => setResendTarget(l)}
                          className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-ink hover:text-white transition-colors ml-auto"
                          data-testid={`lead-resend-${l.id}`}
                        >
                          Resend book
                        </button>
                      )}
                    </div>
                    {(resendErrors[l.id] || (dvOf(l) === "failed" && l.delivery_error)) && (
                      <p className="mt-1 text-sm text-red-600 break-words" data-testid={`lead-resend-error-${l.id}`}>
                        {resendErrors[l.id] || l.delivery_error}
                      </p>
                    )}
                    {expandedLead === l.id && (
                      <div className="mt-3 border-t border-hairline pt-3" data-testid={`lead-history-${l.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                          <span className="label-mono text-muted">History</span>
                          <span className="label-mono text-muted" data-testid={`lead-download-count-${l.id}`}>
                            Downloads: {l.download_count || 0}
                          </span>
                          {!l.token_revoked && (revokeConfirm === l.id ? (
                            <span className="flex items-center gap-2" data-testid={`lead-revoke-inline-${l.id}`}>
                              <span className="label-mono text-red-600">Revoke this download link?</span>
                              <button
                                onClick={() => revokeToken(l)}
                                className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                                data-testid={`lead-revoke-confirm-${l.id}`}
                              >
                                Revoke
                              </button>
                              <button
                                onClick={() => setRevokeConfirm(null)}
                                className="label-mono border border-hairline px-2 py-0.5 hover:bg-surface transition-colors"
                                data-testid={`lead-revoke-cancel-${l.id}`}
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setRevokeConfirm(l.id)}
                              className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                              data-testid={`lead-revoke-${l.id}`}
                            >
                              Revoke link
                            </button>
                          ))}
                          <label className="label-mono text-muted flex items-center gap-2">
                            Follow-up
                            <select
                              value={fuOf(l)}
                              onChange={(e) => changeFollowup(l, e.target.value)}
                              className="bg-transparent border-b border-hairline py-0.5 outline-none label-mono"
                              data-testid={`lead-followup-select-${l.id}`}
                            >
                              {Object.entries(FOLLOWUP_LABELS).map(([k, v]) => (
                                <option key={k} value={k}>{v}</option>
                              ))}
                            </select>
                          </label>
                          <span className="label-mono text-muted flex items-center gap-2" data-testid={`lead-phone-${l.id}`}>
                            Phone
                            {editPhoneId === l.id ? (
                              <>
                                <input
                                  type="text"
                                  value={phoneDraft}
                                  onChange={(e) => setPhoneDraft(e.target.value)}
                                  className="bg-transparent border-b border-hairline focus:border-ink py-0.5 text-sm outline-none w-40 text-ink"
                                  data-testid={`lead-phone-input-${l.id}`}
                                />
                                <button
                                  onClick={() => saveLeadPhone(l)}
                                  disabled={savingPhone}
                                  className="label-mono border border-hairline px-2 py-0.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-40"
                                  data-testid={`lead-phone-save-${l.id}`}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => setEditPhoneId(null)}
                                  className="label-mono border border-hairline px-2 py-0.5 hover:bg-surface transition-colors"
                                  data-testid={`lead-phone-cancel-${l.id}`}
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                {l.phone && (
                                  <span
                                    className="inline-block px-2 py-0.5 text-sm rounded text-ink"
                                    style={{ backgroundColor: "#F2F1EF", border: "1px solid #D4D2CF" }}
                                    data-testid={`lead-phone-value-${l.id}`}
                                  >
                                    {l.phone}
                                  </span>
                                )}
                                <button
                                  onClick={() => startEditPhone(l)}
                                  className="label-mono border border-hairline px-2 py-0.5 hover:bg-ink hover:text-white transition-colors"
                                  data-testid={`lead-phone-edit-${l.id}`}
                                >
                                  Edit
                                </button>
                              </>
                            )}
                          </span>
                        </div>
                        {!leadHistory[l.id] ? (
                          <p className="text-sm text-muted">Loading…</p>
                        ) : leadHistory[l.id].length === 0 ? (
                          <p className="text-sm text-muted">No recorded events.</p>
                        ) : (
                          <div className="divide-y divide-hairline">
                            {leadHistory[l.id].map((ev, i) => (
                              <div key={i} className="py-2 grid grid-cols-12 gap-2 items-baseline">
                                <div className="col-span-6 md:col-span-3 label-mono text-muted">{fmt(ev.created_at)}</div>
                                <div className="col-span-6 md:col-span-4 text-sm text-ink">{eventLabel(ev)}</div>
                        <div className="col-span-6 md:col-span-3 label-mono text-muted break-all">
                          {eventDetailText(ev)}
                        </div>
                                <div className="col-span-6 md:col-span-2 label-mono text-muted">{ev.actor === "admin" ? "Admin" : "Auto"}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : tab === "pages" ? (
          <div data-testid="table-pages">
            <div className="flex items-center gap-4 mb-8 flex-wrap">
              <h2 className="font-display text-3xl md:text-4xl">Site pages</h2>
              {orderSaved && (
                <span className="label-mono text-muted" data-testid="pages-order-saved">Order saved.</span>
              )}
            </div>
            <div className="border-t border-hairline divide-y divide-hairline">
              {orderedPages.map((path, idx) => (
                <div
                  key={path}
                  draggable={pageDragArmed === idx}
                  onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; setPageDragIdx(idx); }}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setPageDragOverIdx(idx); }}
                  onDragLeave={() => setPageDragOverIdx((v) => (v === idx ? null : v))}
                  onDrop={(e) => { e.preventDefault(); dropPage(idx); }}
                  onDragEnd={() => { setPageDragIdx(null); setPageDragOverIdx(null); setPageDragArmed(null); }}
                  className={`py-4 grid grid-cols-12 gap-4 md:gap-6 items-center transition-colors ${pageDragOverIdx === idx && pageDragIdx !== null && pageDragIdx !== idx ? "bg-surface" : ""} ${pageDragIdx === idx ? "opacity-40" : ""}`}
                  data-testid={`page-row-${path}`}
                >
                  <div className="col-span-8 md:col-span-9 flex items-center gap-3 min-w-0">
                    <span
                      onMouseDown={() => setPageDragArmed(idx)}
                      onMouseUp={() => setPageDragArmed(null)}
                      onTouchStart={() => setPageDragArmed(idx)}
                      onTouchEnd={() => setPageDragArmed(null)}
                      className="text-muted hover:text-ink cursor-grab active:cursor-grabbing shrink-0"
                      aria-hidden="true"
                      data-testid={`page-drag-handle-${path}`}
                    >
                      <GripVertical size={16} />
                    </span>
                    <span className="label-mono text-ink break-all">{path}</span>
                  </div>
                  <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-2">
                    <button
                      onClick={() => movePage(idx, -1)}
                      disabled={idx === 0}
                      aria-label={`Move ${path} up`}
                      className="text-muted hover:text-ink transition-colors disabled:opacity-25 disabled:hover:text-muted p-1"
                      data-testid={`page-move-up-${path}`}
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      onClick={() => movePage(idx, 1)}
                      disabled={idx === orderedPages.length - 1}
                      aria-label={`Move ${path} down`}
                      className="text-muted hover:text-ink transition-colors disabled:opacity-25 disabled:hover:text-muted p-1"
                      data-testid={`page-move-down-${path}`}
                    >
                      <ChevronDown size={16} />
                    </button>
                    <a
                      href={path}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
                      data-testid={`page-open-${path}`}
                    >
                      Open ↗
                    </a>
                  </div>
                </div>
              ))}
            </div>

            {/* ---------- Redirects ---------- */}
            <div className="mt-16 md:mt-20">
              <h2 className="font-display text-3xl md:text-4xl mb-8">
                Redirects <span className="label-mono text-muted align-middle ml-2">({redirects.length})</span>
              </h2>

              <CampaignLinkBuilder
                api={API}
                csrf={csrf}
                redirects={redirects}
                onCreated={(r) => setRedirects((prev) => [r, ...prev])}
              />

              <form onSubmit={submitRedirect} className="border border-hairline p-6 mb-10" data-testid="redirect-form">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                  <div className="md:col-span-4">
                    <label className="label-mono text-muted block mb-2">Source path</label>
                    <input
                      type="text"
                      value={redirectForm.source}
                      onChange={(e) => { setRedirectFormError(""); setRedirectForm((f) => ({ ...f, source: e.target.value })); }}
                      placeholder="/size-lock"
                      className="w-full bg-transparent border-b border-ink py-2 outline-none"
                      data-testid="redirect-form-source"
                    />
                  </div>
                  <div className="md:col-span-5">
                    <label className="label-mono text-muted block mb-2">Destination</label>
                    <input
                      type="text"
                      value={redirectForm.destination}
                      onChange={(e) => { setRedirectFormError(""); setRedirectForm((f) => ({ ...f, destination: e.target.value })); }}
                      placeholder="/notify?tool=size-lock"
                      className="w-full bg-transparent border-b border-ink py-2 outline-none"
                      data-testid="redirect-form-destination"
                    />
                  </div>
                  <div className="md:col-span-1">
                    <label className="label-mono text-muted block mb-2">Type</label>
                    <select
                      value={redirectForm.type}
                      onChange={(e) => setRedirectForm((f) => ({ ...f, type: Number(e.target.value) }))}
                      className="w-full bg-transparent border-b border-ink py-2 outline-none label-mono"
                      data-testid="redirect-form-type"
                    >
                      <option value={301}>301</option>
                      <option value={302}>302</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 flex gap-2 justify-end">
                    <button
                      type="submit"
                      className="label-mono bg-ink text-white px-4 py-2 hover:bg-black transition-colors"
                      data-testid="redirect-form-submit"
                    >
                      {redirectForm.id ? "Save" : "Add redirect"}
                    </button>
                    {redirectForm.id && (
                      <button
                        type="button"
                        onClick={cancelRedirectEdit}
                        className="label-mono border border-hairline px-3 py-2 hover:bg-surface transition-colors"
                        data-testid="redirect-form-cancel"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
                {redirectFormError && (
                  <p className="mt-4 text-sm text-red-600" data-testid="redirect-form-error">{redirectFormError}</p>
                )}
                {!redirectFormError && redirectChainWarning() && (
                  <p className="mt-4 text-sm text-amber-700" data-testid="redirect-form-warning">{redirectChainWarning()}</p>
                )}
              </form>

              {redirects.length === 0 ? (
                <p className="text-muted" data-testid="redirects-empty">No redirects configured.</p>
              ) : (
                <div className="border-t border-hairline divide-y divide-hairline">
                  {redirects.map((r) => (
                    <div key={r.id} className="py-4 grid grid-cols-12 gap-4 md:gap-6 items-baseline" data-testid={`redirect-row-${r.id}`}>
                      <div className="col-span-6 md:col-span-4 label-mono text-ink break-all" data-testid={`redirect-source-${r.id}`}>
                        {r.source}
                        {campaignMeta(r.destination) && (
                          <div className="text-muted mt-1" data-testid={`redirect-campaign-${r.id}`}>
                            {campaignMeta(r.destination).source}
                            {campaignMeta(r.destination).campaign ? ` · ${campaignMeta(r.destination).campaign}` : ""}
                          </div>
                        )}
                      </div>
                      <div className="col-span-6 md:col-span-5 text-ink break-all" data-testid={`redirect-destination-${r.id}`}>{r.destination}</div>
                      <div className="col-span-6 md:col-span-1 label-mono text-muted" data-testid={`redirect-type-${r.id}`}>{r.type}</div>
                      <div className="col-span-6 md:col-span-2 flex flex-wrap gap-2 justify-end">
                        <button
                          onClick={() => editRedirect(r)}
                          className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
                          data-testid={`redirect-edit-${r.id}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteRedirectTarget(r)}
                          className="label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors"
                          data-testid={`redirect-delete-${r.id}`}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ---------- Hardcoded Redirects (read-only) ---------- */}
            <div className="mt-16 md:mt-20">
              <h2 className="font-display text-3xl md:text-4xl mb-3">
                Redirects &mdash; hardcoded <span className="label-mono text-muted align-middle ml-2">({HARDCODED_REDIRECTS.length})</span>
              </h2>
              <p className="text-sm text-muted mb-8" data-testid="hardcoded-redirects-note">
                These are defined in code and require a code change to modify. They cannot be edited or deleted from the admin.
              </p>
              <div className="border-t border-hairline divide-y divide-hairline">
                {HARDCODED_REDIRECTS.map((r) => (
                  <div
                    key={r.source}
                    className="py-4 grid grid-cols-12 gap-4 md:gap-6 items-baseline bg-surface/40"
                    data-testid={`hardcoded-redirect-row-${r.source}`}
                  >
                    <div className="col-span-6 md:col-span-4 label-mono text-ink break-all">{r.source}</div>
                    <div className="col-span-6 md:col-span-5 text-ink break-all">{r.destination}</div>
                    <div className="col-span-6 md:col-span-1 label-mono text-muted">{r.type}</div>
                    <div className="col-span-6 md:col-span-2 flex justify-end">
                      <span
                        className="label-mono border border-hairline text-muted px-3 py-1.5 cursor-not-allowed"
                        aria-disabled="true"
                        title="Defined in code — not editable from admin"
                        data-testid={`hardcoded-redirect-locked-${r.source}`}
                      >
                        In code
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : tab === "dashboard" ? (
          (() => {
            const dashInvalid = Boolean(dashFrom && dashTo && dashFrom > dashTo);
            const rFrom = dashInvalid ? "" : dashFrom;
            const rTo = dashInvalid ? "" : dashTo;
            const rangeActive = Boolean(rFrom || rTo);
            const fromMs = rFrom ? Date.parse(`${rFrom}T00:00:00.000Z`) : null;
            const toMs = rTo ? Date.parse(`${rTo}T23:59:59.999Z`) : null;
            const inRange = (iso) => {
              if (!rangeActive) return true;
              if (!iso) return false;
              const t = Date.parse(iso);
              if (Number.isNaN(t)) return false;
              if (fromMs !== null && t < fromMs) return false;
              if (toMs !== null && t > toMs) return false;
              return true;
            };
            const fmtUtcDay = (d) =>
              new Date(`${d}T00:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
            const rangeLabel = !rangeActive
              ? "All time"
              : rFrom && rTo
                ? `${fmtUtcDay(rFrom)} – ${fmtUtcDay(rTo)}`
                : rFrom
                  ? `From ${fmtUtcDay(rFrom)}`
                  : `Through ${fmtUtcDay(rTo)}`;
            const fLeads = rangeActive ? leads.filter((x) => inRange(x.created_at)) : leads;
            const addedSince = (from) => leads.filter((x) => localDayStr(x.created_at) >= from).length;
            const sparkDays = (() => {
              const p = (n) => String(n).padStart(2, "0");
              const toStr = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
              const parse = (s) => { const [y, m, dd] = s.split("-").map(Number); return new Date(y, m - 1, dd); };
              const end = rTo || todayStr();
              let start = rFrom || toStr(new Date(parse(end).getTime() - 29 * 86400000));
              if (start > end) start = end;
              const counts = {};
              leads.forEach((x) => { const d = localDayStr(x.created_at); counts[d] = (counts[d] || 0) + 1; });
              const days = [];
              const cur = parse(start);
              const endD = parse(end);
              let guard = 0;
              while (cur <= endD && guard < 400) { const k = toStr(cur); days.push({ day: k, count: counts[k] || 0 }); cur.setDate(cur.getDate() + 1); guard += 1; }
              return days;
            })();
            const dLeads = rangeActive ? leads.filter((x) => inRange(x.book_sent_at || x.last_book_email_at)) : leads;
            const dvCounts = { not_sent: 0, sent: 0, delivered: 0, failed: 0, bounced: 0 };
            dLeads.forEach((x) => { dvCounts[dvOf(x)] = (dvCounts[dvOf(x)] || 0) + 1; });
            const booksSent = dLeads.filter((x) => x.book_sent_at || ["sent", "delivered", "bounced"].includes(dvOf(x))).length;
            const attention = dvCounts.failed + dvCounts.bounced;
            const deliveryRate = dvCounts.delivered > 0 && booksSent > 0 ? `${Math.round((dvCounts.delivered / booksSent) * 100)}%` : "—";
            const downloaded = leads.filter((x) => x.downloaded_at && inRange(x.downloaded_at)).length;
            const notDownloaded = rangeActive
              ? fLeads.filter((x) => !(x.downloaded_at && inRange(x.downloaded_at))).length
              : leads.length - downloaded;
            const downloadedDelivered = leads.filter((x) => x.downloaded_at && inRange(x.downloaded_at) && dvOf(x) === "delivered").length;
            const downloadRate = dvCounts.delivered > 0 ? `${Math.round((downloadedDelivered / dvCounts.delivered) * 100)}%` : "—";
            const srcCounts = {};
            fLeads.forEach((x) => leadTags(x).forEach((t) => { srcCounts[t] = (srcCounts[t] || 0) + 1; }));
            const KNOWN_SOURCES = ["landing", "ebook", "book", "review", "cage_system"];
            const otherSources = Object.keys(srcCounts).filter((t) => !KNOWN_SOURCES.includes(t)).sort();
            const rvAll = reviewsData || [];
            const rv = rangeActive ? rvAll.filter((r) => inRange(r.created_at)) : rvAll;
            const rvLive = rv.filter((r) => !r.deleted_at);
            const visitsBySource = (attribData && attribData.visits_by_source) || {};
            const visitsByCampaign = (attribData && attribData.visits_by_campaign) || {};
            const channels = {};
            const chGet = (k) => channels[k] || (channels[k] = { visits: 0, leads: 0, downloads: 0 });
            Object.entries(visitsBySource).forEach(([s, v]) => {
              const k = s === "direct" ? "__direct__" : SAFE_UTM.test(s) ? s.toLowerCase() : "__other__";
              chGet(k).visits += v;
            });
            leads.forEach((x) => {
              const s = x.first_touch_source;
              const k = !s || s === "direct" ? "__direct__" : SAFE_UTM.test(s) ? s.toLowerCase() : "__other__";
              const c = chGet(k);
              if (inRange(x.created_at)) c.leads += 1;
              if (x.downloaded_at && inRange(x.downloaded_at)) c.downloads += 1;
            });
            ["youtube", "reddit", "__direct__", "__other__"].forEach(chGet);
            Object.entries(attrOverrides).forEach(([mk, v]) => {
              const i = mk.indexOf(":");
              if (mk.slice(0, i) !== "channel") return;
              const kk = mk.slice(i + 1);
              const b = channels[kk] || { visits: 0, leads: 0, downloads: 0 };
              channels[kk] = { ...b, visits: Math.max(0, b.visits + (v.offset_visits || 0)), leads: Math.max(0, b.leads + (v.offset_leads || 0)), downloads: Math.max(0, b.downloads + (v.offset_downloads || 0)) };
            });
            const chOrder = { __direct__: 1, __other__: 2 };
            const chRows = Object.entries(channels).sort(
              (a, b) => (chOrder[a[0]] || 0) - (chOrder[b[0]] || 0) || b[1].visits - a[1].visits,
            );
            const chLabel = (k) => (k === "__direct__" ? "Direct / Unknown" : k === "__other__" ? "Other" : channelLabel(k));
            const rate = (num, den) => (den > 0 ? `${Math.round((num / den) * 100)}%` : "—");
            const campaigns = {};
            const cpGet = (k) => campaigns[k] || (campaigns[k] = { visits: 0, leads: 0, downloads: 0 });
            Object.entries(visitsByCampaign).forEach(([c, v]) => {
              const k = c === "__unspecified__" ? "__unspecified__" : SAFE_UTM.test(c) ? c.toLowerCase() : null;
              if (k) cpGet(k).visits += v;
            });
            leads.forEach((x) => {
              if (!x.first_touch_source) return;
              const c = x.first_touch_campaign;
              const k = !c ? "__unspecified__" : SAFE_UTM.test(c) ? c.toLowerCase() : null;
              if (!k) return;
              const row = cpGet(k);
              if (inRange(x.created_at)) row.leads += 1;
              if (x.downloaded_at && inRange(x.downloaded_at)) row.downloads += 1;
            });
            Object.entries(attrOverrides).forEach(([mk, v]) => {
              const i = mk.indexOf(":");
              if (mk.slice(0, i) !== "campaign") return;
              const kk = mk.slice(i + 1);
              const b = campaigns[kk] || { visits: 0, leads: 0, downloads: 0 };
              campaigns[kk] = { ...b, visits: Math.max(0, b.visits + (v.offset_visits || 0)), leads: Math.max(0, b.leads + (v.offset_leads || 0)), downloads: Math.max(0, b.downloads + (v.offset_downloads || 0)) };
            });
            const cpRows = Object.entries(campaigns).sort(
              (a, b) => ((a[0] === "__unspecified__") - (b[0] === "__unspecified__")) || b[1].visits - a[1].visits,
            );
            const cpLabel = (k) => (k === "__unspecified__" ? "Unspecified" : titleize(k));
            const BASELINE_CHANNELS = ["youtube", "reddit", "__direct__", "__other__"];
            const openChannelDelete = (k, c) => {
              const src = k === "__direct__" ? "direct" : k;
              setAttrDeleteTarget({
                type: "channel",
                key: k,
                label: chLabel(k),
                visits: c.visits,
                firstCount: leads.filter((l) => l.first_touch_source === src).length,
                lastCount: leads.filter((l) => l.last_touch_source === src).length,
                isBaseline: BASELINE_CHANNELS.includes(k),
              });
            };
            const openCampaignDelete = (k, c) => {
              setAttrDeleteTarget({
                type: "campaign",
                key: k,
                label: cpLabel(k),
                visits: c.visits,
                firstCount: leads.filter((l) => l.first_touch_campaign === k).length,
                lastCount: leads.filter((l) => l.last_touch_campaign === k).length,
                isBaseline: false,
              });
            };
            // Clear the Direct/Unknown row to zero across all three columns by
            // setting each offset to the negative of its raw value.
            const clearChannelToZero = (k, c) => {
              const ex = attrOverrides[`channel:${k}`] || {};
              saveAttrOverride({
                type: "channel", key: k, label: chLabel(k),
                visits: 0, leads: 0, downloads: 0, unique: 0,
                rawVisits: c.visits - (ex.offset_visits || 0),
                rawLeads: c.leads - (ex.offset_leads || 0),
                rawDownloads: (c.downloads || 0) - (ex.offset_downloads || 0),
                rawUnique: 0,
              });
            };
            const EDIT_CAMPAIGNS = ["organic-search", "__unspecified__"];
            const openEdit = (type, k, label, c) => {
              const ex = attrOverrides[`${type}:${k}`] || {};
              setAttrEditTarget({
                type, key: k, label,
                visits: c.visits, leads: c.leads, downloads: c.downloads,
                rawVisits: c.visits - (ex.offset_visits || 0),
                rawLeads: c.leads - (ex.offset_leads || 0),
                rawDownloads: c.downloads - (ex.offset_downloads || 0),
              });
            };
            const editBtn = (type, k, label, c) => (
              <button
                onClick={() => openEdit(type, k, label, c)}
                className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                data-testid={`dash-${type}-edit-${k}`}
              >
                Edit
              </button>
            );
            const channelAction = (k, c) => (
              <div className="flex flex-col items-end gap-1">
                {editBtn("channel", k, chLabel(k), c)}
                <button
                  onClick={() => (k === "__direct__" ? clearChannelToZero(k, c) : openChannelDelete(k, c))}
                  className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                  data-testid={`dash-channel-${k === "__direct__" ? "clear" : "delete"}-${k}`}
                >
                  {BASELINE_CHANNELS.includes(k) ? "Clear" : "Delete"}
                </button>
              </div>
            );
            const campaignAction = (k, c) => {
              const canEdit = EDIT_CAMPAIGNS.includes(k);
              const canDelete = k !== "__unspecified__";
              if (!canEdit && !canDelete) return null;
              return (
                <div className="flex flex-col items-end gap-1">
                  {canEdit ? editBtn("campaign", k, cpLabel(k), c) : null}
                  {canDelete ? (
                    <button
                      onClick={() => openCampaignDelete(k, c)}
                      className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                      data-testid={`dash-campaign-delete-${k}`}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              );
            };
            const totalVisits = Object.values(visitsBySource).reduce((a, b) => a + b, 0);
            const attributedLeadCount = leads.filter((l) => l.first_touch_source || l.last_touch_source).length;
            const attrTable = (title, rows, labelFn, nameCol, testPrefix, rowAction, rowType) => {
              // Best converter = highest Visit→Lead rate among rows with >= 10 visits (skips TOTAL).
              const winner = (() => {
                const eligible = rows
                  .filter(([, c]) => c.visits >= 10)
                  .map(([k, c]) => ({ k, r: c.leads / c.visits, v: c.visits }))
                  .sort((a, b) => b.r - a.r || b.v - a.v);
                const top = eligible[0];
                if (!top || top.r <= 0) return null;
                return { key: top.k, rate: top.r, next: eligible[1] || null };
              })();
              const winnerKey = winner ? winner.key : null;
              const pctFmt = (r) => `${Math.round(r * 100)}%`;
              const winnerTip = winner
                ? `Highest Visit→Lead rate at ${pctFmt(winner.rate)}` +
                  (winner.next
                    ? `, ahead of the next best (${labelFn(winner.next.k)}) at ${pctFmt(winner.next.r)}.`
                    : `, and the only ${nameCol.toLowerCase()} with 10+ visits.`)
                : "";
              return (
              <div className="mb-10 max-w-3xl" key={title}>
                <h3 className="label-mono text-muted mb-3">{title}</h3>
                <div className="border-t border-hairline divide-y divide-hairline">
                  <div className="py-2 grid grid-cols-12 gap-2 items-baseline">
                    <span className="col-span-3 label-mono text-muted">{nameCol}</span>
                    <span className="col-span-1 label-mono text-muted text-right">Visits</span>
                    <span className="col-span-1 label-mono text-muted text-right">Leads</span>
                    <span className="col-span-2 label-mono text-muted text-right">Downloads</span>
                    <span className="col-span-2 label-mono text-muted text-right">Visit → Lead</span>
                    <span className="col-span-2 label-mono text-muted text-right pr-4 md:pr-6">Lead → Download</span>
                    <span className="col-span-1" />
                  </div>
                  {rows.length === 0 ? (
                    <div className="py-3 text-sm text-muted" data-testid={`${testPrefix}-empty`}>No attributed activity yet.</div>
                  ) : (
                    rows.map(([k, c]) => {
                      const confirming = attrDeleteTarget && attrDeleteTarget.type === rowType && attrDeleteTarget.key === k;
                      const isWinner = k === winnerKey;
                      if (confirming) {
                        const t = attrDeleteTarget;
                        const leadsAffected = t.type === "channel" ? t.firstCount : t.firstCount + t.lastCount;
                        const resetLabel = t.type === "channel" ? "Direct / Unknown" : "Unspecified";
                        const clause = leadsAffected === 0 ? "no leads affected" : `${leadsAffected} lead${leadsAffected === 1 ? "" : "s"} → ${resetLabel}`;
                        return (
                          <div key={k} className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid={`${testPrefix}-${k}`}>
                            <span className="col-span-3 text-sm text-ink break-all">{labelFn(k)}</span>
                            <span className="col-span-9 flex items-baseline justify-end gap-3 whitespace-nowrap" data-testid={`${testPrefix}-confirm-strip-${k}`}>
                              <span className="label-mono text-muted">
                                {t.visits} visit{t.visits === 1 ? "" : "s"} · {clause} · all records, ignoring the date filter
                              </span>
                              <button
                                onClick={confirmAttrDelete}
                                className="label-mono bg-red-600 text-white px-2 py-0.5 hover:bg-red-700 transition-colors"
                                data-testid={`${testPrefix}-confirm-${k}`}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setAttrDeleteTarget(null)}
                                className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                                data-testid={`${testPrefix}-cancel-${k}`}
                              >
                                Cancel
                              </button>
                            </span>
                          </div>
                        );
                      }
                      const editing = attrEditTarget && attrEditTarget.type === rowType && attrEditTarget.key === k;
                      if (editing) {
                        const e = attrEditTarget;
                        const setF = (f, val) => setAttrEditTarget((p) => ({ ...p, [f]: Math.max(0, parseInt(val, 10) || 0) }));
                        const numInput = (f, ph) => (
                          <label className="flex items-baseline gap-1">
                            <span className="label-mono text-muted">{ph}</span>
                            <input
                              type="number"
                              min="0"
                              value={e[f]}
                              onChange={(ev) => setF(f, ev.target.value)}
                              className="w-16 border border-hairline px-1 py-0.5 text-right font-display text-lg"
                              data-testid={`${testPrefix}-edit-${f}-${k}`}
                            />
                          </label>
                        );
                        return (
                          <div key={k} className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid={`${testPrefix}-${k}`}>
                            <span className="col-span-3 text-sm text-ink break-all">{labelFn(k)}</span>
                            <span className="col-span-9 flex items-center justify-end gap-3 flex-wrap" data-testid={`${testPrefix}-edit-strip-${k}`}>
                              {numInput("visits", "Visits")}
                              {numInput("leads", "Leads")}
                              {numInput("downloads", "Downloads")}
                              <button
                                onClick={() => saveAttrOverride(e)}
                                className="label-mono bg-ink text-white px-2 py-0.5 hover:bg-leaf transition-colors"
                                data-testid={`${testPrefix}-edit-save-${k}`}
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setAttrEditTarget(null)}
                                className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                                data-testid={`${testPrefix}-edit-cancel-${k}`}
                              >
                                Cancel
                              </button>
                              {attrOverrides[`${rowType}:${k}`] ? (
                                <button
                                  onClick={() => resetAttrOverride(e)}
                                  className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                                  data-testid={`${testPrefix}-edit-reset-${k}`}
                                >
                                  Reset to auto
                                </button>
                              ) : null}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div key={k} className={`py-3 grid grid-cols-12 gap-2 items-baseline ${isWinner ? "bg-emerald-50 border-l-2 border-emerald-500 pl-2 -ml-2" : ""}`} data-testid={`${testPrefix}-${k}`}>
                          <span className="col-span-3 text-sm text-ink break-all flex flex-wrap items-center gap-x-2 gap-y-1">
                            <span>{labelFn(k)}</span>
                            {isWinner && (
                              <span className="label-mono text-emerald-700 border border-emerald-300 px-1 leading-tight whitespace-nowrap cursor-help" title={winnerTip} data-testid={`${testPrefix}-top-converter`}>
                                Top converter
                              </span>
                            )}
                          </span>
                          <span className="col-span-1 font-display text-lg text-right">{c.visits}</span>
                          <span className="col-span-1 font-display text-lg text-right">{c.leads}</span>
                          <span className="col-span-2 font-display text-lg text-right">{c.downloads}</span>
                          <span className="col-span-2 font-display text-lg text-right">{rate(c.leads, c.visits)}</span>
                          <span className="col-span-2 font-display text-lg text-right pr-4 md:pr-6">{rate(c.downloads, c.leads)}</span>
                          <span className="col-span-1 text-right">{rowAction ? rowAction(k, c) : null}</span>
                        </div>
                      );
                    })
                  )}
                  {rows.length > 0 && (() => {
                    const tv = rows.reduce((s, [, c]) => s + c.visits, 0);
                    const tl = rows.reduce((s, [, c]) => s + c.leads, 0);
                    const td = rows.reduce((s, [, c]) => s + c.downloads, 0);
                    return (
                      <div className="py-3 grid grid-cols-12 gap-2 items-baseline border-t-2 border-ink" data-testid={`${testPrefix}-total`}>
                        <span className="col-span-3 label-mono text-ink">TOTAL</span>
                        <span className="col-span-1 font-display text-lg text-right" data-testid={`${testPrefix}-total-visits`}>{tv}</span>
                        <span className="col-span-1 font-display text-lg text-right" data-testid={`${testPrefix}-total-leads`}>{tl}</span>
                        <span className="col-span-2 font-display text-lg text-right" data-testid={`${testPrefix}-total-downloads`}>{td}</span>
                        <span className="col-span-2 font-display text-lg text-right" data-testid={`${testPrefix}-total-v2l`}>{rate(tl, tv)}</span>
                        <span className="col-span-2 font-display text-lg text-right pr-4 md:pr-6" data-testid={`${testPrefix}-total-l2d`}>{rate(td, tl)}</span>
                        <span className="col-span-1" />
                      </div>
                    );
                  })()}
                </div>
              </div>
            );
            };
            const metric = (label, value, testid) => (
              <div key={testid} className="py-3 flex items-baseline justify-between gap-4" data-testid={testid}>
                <span className="text-sm text-ink">{label}</span>
                <span className="font-display text-xl">{value}</span>
              </div>
            );
            const section = (title, rows) => (
              <div className="mb-10 max-w-2xl" key={title}>
                <h3 className="label-mono text-muted mb-3">{title}</h3>
                <div className="border-t border-hairline divide-y divide-hairline">{rows}</div>
              </div>
            );
            const exportDashboardCsv = () => {
              const gen = new Date().toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
              const val = (v) => (v === "—" ? "" : v);
              const rows = [
                ...(rangeActive ? [["Period", "Selected range", rangeLabel]] : []),
                ["Lead totals", "Total unique ebook leads", fLeads.length],
                ...(rangeActive
                  ? [["Lead totals", `Added ${rangeLabel}`, fLeads.length]]
                  : [
                      ["Lead totals", "Added today", addedSince(todayStr())],
                      ["Lead totals", "Added in the last 7 days", addedSince(daysAgoStr(6))],
                      ["Lead totals", "Added in the last 30 days", addedSince(daysAgoStr(29))],
                    ]),
                ["Lead totals", "Cage System early list", srcCounts["cage_system"] || 0],
                ["Delivery", "Book emails sent", booksSent],
                ["Delivery", "Delivered", dvCounts.delivered],
                ["Delivery", "Failed", dvCounts.failed],
                ["Delivery", "Bounced", dvCounts.bounced],
                ["Delivery", "Delivery rate", val(deliveryRate)],
                ["Delivery", "Failed or bounced — needs attention", attention],
                ["Downloads", "Total downloaded", downloaded],
                ["Downloads", "Total not downloaded", notDownloaded],
                ["Downloads", "Download rate among delivered", val(downloadRate)],
                ...chRows.flatMap(([k, c]) => [
                  ["Acquisition channels", `${chLabel(k)} — visits`, c.visits],
                  ["Acquisition channels", `${chLabel(k)} — leads`, c.leads],
                  ["Acquisition channels", `${chLabel(k)} — downloads`, c.downloads],
                  ["Acquisition channels", `${chLabel(k)} — visit to lead`, val(rate(c.leads, c.visits))],
                  ["Acquisition channels", `${chLabel(k)} — lead to download`, val(rate(c.downloads, c.leads))],
                ]),
                ...cpRows.flatMap(([k, c]) => [
                  ["Campaigns", `${cpLabel(k)} — visits`, c.visits],
                  ["Campaigns", `${cpLabel(k)} — leads`, c.leads],
                  ["Campaigns", `${cpLabel(k)} — downloads`, c.downloads],
                  ["Campaigns", `${cpLabel(k)} — visit to lead`, val(rate(c.leads, c.visits))],
                  ["Campaigns", `${cpLabel(k)} — lead to download`, val(rate(c.downloads, c.leads))],
                ]),
                ...(deviceData
                  ? [
                      ["Traffic by device", "Mobile — unique visitors", deviceData.mobile?.unique_visitors ?? 0],
                      ["Traffic by device", "Mobile — visits", deviceData.mobile?.visits ?? 0],
                      ["Traffic by device", "Desktop — unique visitors", deviceData.desktop?.unique_visitors ?? 0],
                      ["Traffic by device", "Desktop — visits", deviceData.desktop?.visits ?? 0],
                      ["Traffic by device", "Total unique visitors", deviceData.total_unique_visitors ?? 0],
                      ["Traffic by device", "Total visits", deviceData.total_visits ?? 0],
                    ]
                  : []),
                ["Signup locations", "Landing", srcCounts["landing"] || 0],
                ["Signup locations", "Ebook", srcCounts["ebook"] || 0],
                ["Signup locations", "Book", srcCounts["book"] || 0],
                ["Signup locations", "Review", srcCounts["review"] || 0],
                ["Signup locations", "Cage System", srcCounts["cage_system"] || 0],
                ...otherSources.map((t) => ["Signup locations", t, srcCounts[t]]),
                ["Reviews", "Pending", rvLive.filter((r) => r.status === "pending").length],
                ["Reviews", "Approved", rvLive.filter((r) => r.status === "approved").length],
                ["Reviews", "Rejected", rvLive.filter((r) => r.status === "rejected").length],
                ["Reviews", "Deleted", rv.filter((r) => r.deleted_at).length],
              ].map((r) => [...r, gen]);
              const csvName = rangeActive
                ? `biology-trader-dashboard-${todayStr()}-${rFrom || "start"}-to-${rTo || "now"}.csv`
                : `biology-trader-dashboard-${todayStr()}.csv`;
              exportCsv(csvName, ["SECTION", "METRIC", "VALUE", "GENERATED_AT"], rows);
              axios.post(
                `${API}/audit/csv-export`,
                { record_type: "dashboard", count: rows.length, action: "dashboard_csv_exported" },
                { headers: { "X-CSRF-Token": csrf }, withCredentials: true },
              ).catch(() => {});
            };
            return (
              <div data-testid="table-dashboard">
                <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
                  <h2 className="font-display text-3xl md:text-4xl">Dashboard</h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleSendDigestNow}
                      disabled={digestSending}
                      className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="dashboard-send-digest"
                    >
                      {digestSending ? "Sending…" : "Send weekly digest now"}
                    </button>
                    <button
                      onClick={exportDashboardCsv}
                      className="label-mono border border-hairline text-ink px-3 py-1.5 hover:bg-ink hover:text-white transition-colors"
                      data-testid="dashboard-export-csv"
                    >
                      Export CSV
                    </button>
                  </div>
                </div>
                {digestMsg && (
                  <p className="mb-6 text-sm text-green-700" data-testid="dashboard-digest-success">{digestMsg}</p>
                )}
                {error && (
                  <p className="mb-6 text-sm text-red-600" data-testid="dashboard-action-error">{error}</p>
                )}
                <div className="mb-8 border border-hairline p-4" data-testid="dash-range-control">
                  <div className="flex flex-wrap items-end gap-4">
                    <label className="label-mono text-muted flex items-center gap-2">
                      From
                      <input
                        type="date"
                        value={dashFrom}
                        onChange={(e) => { setDashFrom(e.target.value); setDashPreset("custom"); }}
                        className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                        data-testid="dash-range-from"
                      />
                    </label>
                    <label className="label-mono text-muted flex items-center gap-2">
                      To
                      <input
                        type="date"
                        value={dashTo}
                        onChange={(e) => { setDashTo(e.target.value); setDashPreset("custom"); }}
                        className="bg-transparent border-b border-ink py-1 outline-none text-ink"
                        data-testid="dash-range-to"
                      />
                    </label>
                    <div className="flex flex-wrap gap-1">
                      {[["all", "All time"], ["this_week", "This week"], ["this_month", "This month"], ["last_month", "Last month"], ["last_30", "Last 30 days"], ["this_year", "This year"], ["today", "Today"], ["yesterday", "Yesterday"], ["three_days_ago", "3 days ago"]].map(([p, lbl]) => (
                        <button
                          key={p}
                          onClick={() => { const r = dashPresetRange(p); setDashFrom(r.from); setDashTo(r.to); setDashPreset(p); }}
                          className={`label-mono px-3 py-1.5 border transition-colors ${dashPreset === p ? "bg-ink text-white border-ink" : "border-hairline text-ink hover:bg-surface"}`}
                          data-testid={`dash-preset-${p}`}
                        >
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                  {dashInvalid ? (
                    <p className="mt-3 text-sm text-red-600" data-testid="dash-range-invalid">
                      FROM is later than TO — fix the dates. Showing all time until then.
                    </p>
                  ) : (
                    <p className="mt-3 label-mono text-muted" data-testid="dash-range-active">Showing: {rangeLabel}</p>
                  )}
                </div>
                {section("Lead totals", [
                  metric("Total website visits", Math.max(0, ((deviceData && deviceData.total_visits) || 0) + Object.entries(attrOverrides).reduce((s, [mk, o]) => s + (mk.startsWith("country:") ? (o.offset_visits || 0) : 0), 0)), "dash-total-website-visits"),
                  metric("Total unique ebook leads", Math.max(0, fLeads.length + Object.values(attrOverrides).reduce((s, o) => s + (o.offset_leads || 0), 0)), "dash-total-leads"),
                  metric("Active list", fLeads.filter((x) => !x.unsubscribed).length, "dash-active-list"),
                  metric("Unsubscribed", fLeads.filter((x) => x.unsubscribed).length, "dash-unsubscribed"),
                  ...(rangeActive
                    ? [metric(`Added ${rangeLabel}`, fLeads.length, "dash-added-range")]
                    : [
                        metric("Added today", addedSince(todayStr()), "dash-added-today"),
                        metric("Added in the last 7 days", addedSince(daysAgoStr(6)), "dash-added-7d"),
                        metric("Added in the last 30 days", addedSince(daysAgoStr(29)), "dash-added-30d"),
                      ]),
                  metric("Cage System early list", srcCounts["cage_system"] || 0, "dash-cage-total"),
                ])}
                {(() => {
                  const now = new Date();
                  const dow = now.getDay(); // 0=Sun, 1=Mon ...
                  const daysSinceMon = (dow + 6) % 7;
                  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysSinceMon);
                  const p = (n) => String(n).padStart(2, "0");
                  const monStr = `${monday.getFullYear()}-${p(monday.getMonth() + 1)}-${p(monday.getDate())}`;
                  const currentWeekCount = leads.filter((x) => localDayStr(x.created_at) >= monStr).length;
                  let streak = 0;
                  if (weeklyGoal > 0) {
                    const dayStrOf = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
                    const weekCount = (start) => {
                      const s = dayStrOf(start);
                      const e = dayStrOf(new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7));
                      return leads.filter((x) => { const d = localDayStr(x.created_at); return d >= s && d < e; }).length;
                    };
                    let k = currentWeekCount >= weeklyGoal ? 0 : 1;
                    while (k <= 520) {
                      const start = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() - 7 * k);
                      if (weekCount(start) >= weeklyGoal) { streak += 1; k += 1; } else break;
                    }
                  }
                  return (
                    <WeeklyGoalLine
                      current={currentWeekCount}
                      streak={streak}
                      goal={weeklyGoal}
                      editing={weeklyGoalEditing}
                      inputValue={weeklyGoalInput}
                      onInputChange={setWeeklyGoalInput}
                      onEdit={() => { setWeeklyGoalEditing(true); setWeeklyGoalError(""); }}
                      onCancel={() => { setWeeklyGoalEditing(false); setWeeklyGoalError(""); setWeeklyGoalInput(weeklyGoal ? String(weeklyGoal) : ""); }}
                      onSave={saveWeeklyGoal}
                      saving={weeklyGoalSaving}
                      error={weeklyGoalError}
                    />
                  );
                })()}
                {sparkDays.length >= 2 && <SignupsSparkline days={sparkDays} />}
                <div className="mb-10 border-t border-hairline pt-6" data-testid="dash-cage-early-access-control">
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-ink font-semibold">Cage System early-access form</div>
                      <div className="text-sm text-muted mt-1">
                        {cageEarlyEnabled
                          ? "Public form is visible on /cage-system."
                          : "Public form is hidden on /cage-system."}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleCageEarly(!cageEarlyEnabled)}
                      disabled={cageEarlySaving}
                      className="label-mono border border-ink text-ink px-4 py-2 hover:bg-ink hover:text-white transition-colors disabled:opacity-60"
                      data-testid="dash-cage-early-access-toggle"
                    >
                      {cageEarlySaving
                        ? "Saving…"
                        : cageEarlyEnabled
                        ? "Hide public form"
                        : "Show public form"}
                    </button>
                  </div>
                  {cageEarlyError && (
                    <p className="mt-3 text-sm text-red-600" data-testid="dash-cage-early-access-error">{cageEarlyError}</p>
                  )}
                </div>
                {section("Delivery", [
                  metric("Book emails sent", booksSent, "dash-books-sent"),
                  metric("Delivered", dvCounts.delivered, "dash-delivered"),
                  metric("Failed", dvCounts.failed, "dash-failed"),
                  metric("Bounced", dvCounts.bounced, "dash-bounced"),
                  metric("Delivery rate", deliveryRate, "dash-delivery-rate"),
                  metric("Failed or bounced — needs attention", attention, "dash-attention"),
                ])}
                {section("Downloads", [
                  metric("Total downloaded", downloaded, "dash-downloaded"),
                  metric("Total not downloaded", notDownloaded, "dash-not-downloaded"),
                  metric("Download rate among delivered", downloadRate, "dash-download-rate"),
                ])}
                {attrTable("Acquisition channels", chRows, chLabel, "Channel", "dash-channel", channelAction, "channel")}
                {attrTable("Campaigns", cpRows, cpLabel, "Campaign", "dash-campaign", campaignAction, "campaign")}
                <div className="mb-10 max-w-2xl" data-testid="tracking-book-link-clicks">
                  <h3 className="label-mono text-muted mb-3">Book link clicks</h3>
                  <div className="border-t border-hairline divide-y divide-hairline">
                    <div className="py-2 grid gap-2 items-baseline" style={{ gridTemplateColumns: "minmax(120px,1.4fr) minmax(64px,1fr) minmax(64px,1fr) minmax(64px,1fr)" }}>
                      <span className="label-mono text-muted">Tool</span>
                      <span className="label-mono text-muted text-right">All time</span>
                      <span className="label-mono text-muted text-right">Last 7 days</span>
                      <span className="label-mono text-muted text-right">Last 30 days</span>
                    </div>
                    {(blNav?.rows || []).map((r) => (
                      <div
                        key={r.slug}
                        className="py-3 grid gap-2 items-baseline"
                        style={{ gridTemplateColumns: "minmax(120px,1.4fr) minmax(64px,1fr) minmax(64px,1fr) minmax(64px,1fr)" }}
                        data-testid={`tracking-bl-row-${r.slug}`}
                      >
                        <span className="text-sm text-ink">{r.slug}</span>
                        <span className="font-display text-xl text-right">{r.all_time.toLocaleString("en-US")}</span>
                        <span className="font-display text-xl text-right">{r.last_7_days.toLocaleString("en-US")}</span>
                        <span className="font-display text-xl text-right">{r.last_30_days.toLocaleString("en-US")}</span>
                      </div>
                    ))}
                    <div
                      className="py-3 grid gap-2 items-baseline border-t-2 border-ink"
                      style={{ gridTemplateColumns: "minmax(120px,1.4fr) minmax(64px,1fr) minmax(64px,1fr) minmax(64px,1fr)" }}
                      data-testid="tracking-bl-row-total"
                    >
                      <span className="label-mono text-ink flex items-center">Total</span>
                      <span className="font-display text-xl text-right">{(blNav?.total?.all_time ?? 0).toLocaleString("en-US")}</span>
                      <span className="font-display text-xl text-right">{(blNav?.total?.last_7_days ?? 0).toLocaleString("en-US")}</span>
                      <span className="font-display text-xl text-right">{(blNav?.total?.last_30_days ?? 0).toLocaleString("en-US")}</span>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-muted" data-testid="tracking-bl-caption">
                    Clicks, not people. One reader clicking twice counts twice.
                  </p>
                </div>
                {(() => {
                  const mob = (deviceData && deviceData.mobile) || { unique_visitors: 0, visits: 0 };
                  const desk = (deviceData && deviceData.desktop) || { unique_visitors: 0, visits: 0 };
                  const uniqTotal = (deviceData && deviceData.total_unique_visitors) || 0;
                  const visitsTotal = (deviceData && deviceData.total_visits) || 0;
                  const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : "—");
                  const startedAt = deviceData && deviceData.tracking_started_at
                    ? new Date(deviceData.tracking_started_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : null;
                  const deviceRow = (label, c, testid) => (
                    <div key={testid} className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid={testid}>
                      <span className="col-span-4 text-sm text-ink">{label}</span>
                      <span className="col-span-2 font-display text-lg text-right">{c.unique_visitors}</span>
                      <span className="col-span-2 font-display text-lg text-right">{pct(c.unique_visitors, uniqTotal)}</span>
                      <span className="col-span-2 font-display text-lg text-right">{c.visits}</span>
                      <span className="col-span-2 font-display text-lg text-right">{pct(c.visits, visitsTotal)}</span>
                    </div>
                  );
                  return (
                    <div className="mb-10 max-w-3xl" data-testid="dash-device-traffic">
                      <h3 className="label-mono text-muted mb-3">Traffic by device</h3>
                      <div className="border-t border-hairline divide-y divide-hairline">
                        <div className="py-2 grid grid-cols-12 gap-2 items-baseline">
                          <span className="col-span-4 label-mono text-muted">Device</span>
                          <span className="col-span-2 label-mono text-muted text-right">Unique</span>
                          <span className="col-span-2 label-mono text-muted text-right">% Unique</span>
                          <span className="col-span-2 label-mono text-muted text-right">Visits</span>
                          <span className="col-span-2 label-mono text-muted text-right">% Visits</span>
                        </div>
                        {deviceRow("Mobile (incl. tablets)", mob, "dash-device-mobile")}
                        {deviceRow("Desktop", desk, "dash-device-desktop")}
                        <div className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid="dash-device-total">
                          <span className="col-span-4 text-sm text-ink">Total</span>
                          <span className="col-span-2 font-display text-lg text-right">{uniqTotal}</span>
                          <span className="col-span-2" />
                          <span className="col-span-2 font-display text-lg text-right">{visitsTotal}</span>
                          <span className="col-span-2" />
                        </div>
                      </div>
                      <p className="mt-3 label-mono text-muted" data-testid="dash-device-since">
                        {startedAt
                          ? `Tracking since ${startedAt} — no device data exists before this date.`
                          : "Device tracking active — data appears as visitors arrive."}
                      </p>
                    </div>
                  );
                })()}
                {(() => {
                  const rawRows = (countryData && countryData.rows) || [];
                  const EDIT_COUNTRIES = ["DE", "US"];
                  const rowMap = {};
                  rawRows.forEach((r) => { rowMap[r.country] = { ...r }; });
                  EDIT_COUNTRIES.forEach((cc) => { if (!rowMap[cc]) rowMap[cc] = { country: cc, unique_visitors: 0, visits: 0 }; });
                  let adjRows = Object.values(rowMap).map((r) => {
                    const ov = attrOverrides[`country:${r.country}`];
                    if (!ov) return r;
                    return {
                      ...r,
                      unique_visitors: Math.max(0, r.unique_visitors + (ov.offset_unique || 0)),
                      visits: Math.max(0, r.visits + (ov.offset_visits || 0)),
                    };
                  });
                  adjRows.sort((a, b) => b.visits - a.visits || a.country.localeCompare(b.country));
                  const visitsTotal = adjRows.reduce((a, r) => a + r.visits, 0);
                  const uniqTotal = adjRows.reduce((a, r) => a + r.unique_visitors, 0);
                  const pct = (n, d) => (d > 0 ? `${Math.round((n / d) * 100)}%` : "—");
                  const startedAt = countryData && countryData.tracking_started_at
                    ? new Date(countryData.tracking_started_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : null;
                  let names;
                  try { names = new Intl.DisplayNames(["en"], { type: "region" }); } catch { names = null; }
                  const countryName = (code) => {
                    if (code === "XX") return "Unknown";
                    try { return (names && names.of(code)) || code; } catch { return code; }
                  };
                  const displayed = adjRows.slice(0, 12);
                  const displayedCodes = new Set(displayed.map((r) => r.country));
                  EDIT_COUNTRIES.forEach((cc) => {
                    if (!displayedCodes.has(cc)) {
                      const r = adjRows.find((x) => x.country === cc);
                      if (r) { displayed.push(r); displayedCodes.add(cc); }
                    }
                  });
                  const rest = adjRows.filter((r) => !displayedCodes.has(r.country));
                  const other = rest.length
                    ? { country: "__other__", unique_visitors: rest.reduce((a, r) => a + r.unique_visitors, 0), visits: rest.reduce((a, r) => a + r.visits, 0) }
                    : null;
                  const maxVisits = adjRows.length ? adjRows[0].visits : 0;
                  const openCountryEdit = (r) => {
                    const ex = attrOverrides[`country:${r.country}`] || {};
                    setAttrEditTarget({
                      type: "country", key: r.country, label: countryName(r.country),
                      unique: r.unique_visitors, visits: r.visits,
                      rawUnique: r.unique_visitors - (ex.offset_unique || 0),
                      rawVisits: r.visits - (ex.offset_visits || 0),
                    });
                  };
                  const countryRow = (label, c, testid, code) => {
                    const editing = attrEditTarget && attrEditTarget.type === "country" && attrEditTarget.key === code;
                    if (editing) {
                      const e = attrEditTarget;
                      const setF = (f, val) => setAttrEditTarget((p) => ({ ...p, [f]: Math.max(0, parseInt(val, 10) || 0) }));
                      const numInput = (f, ph) => (
                        <label className="flex items-baseline gap-1">
                          <span className="label-mono text-muted">{ph}</span>
                          <input
                            type="number"
                            min="0"
                            value={e[f]}
                            onChange={(ev) => setF(f, ev.target.value)}
                            className="w-16 border border-hairline px-1 py-0.5 text-right font-display text-lg"
                            data-testid={`dash-country-edit-${f}-${code}`}
                          />
                        </label>
                      );
                      return (
                        <div key={testid} className="py-3 grid grid-cols-12 gap-2 items-center" data-testid={testid}>
                          <span className="col-span-3 text-sm text-ink break-all">{label}</span>
                          <span className="col-span-9 flex items-center justify-end gap-3 flex-wrap" data-testid={`dash-country-edit-strip-${code}`}>
                            {numInput("unique", "Unique")}
                            {numInput("visits", "Visits")}
                            <button
                              onClick={() => saveAttrOverride(e)}
                              className="label-mono bg-ink text-white px-2 py-0.5 hover:bg-leaf transition-colors"
                              data-testid={`dash-country-edit-save-${code}`}
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setAttrEditTarget(null)}
                              className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                              data-testid={`dash-country-edit-cancel-${code}`}
                            >
                              Cancel
                            </button>
                            {attrOverrides[`country:${code}`] ? (
                              <button
                                onClick={() => resetAttrOverride(e)}
                                className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                                data-testid={`dash-country-edit-reset-${code}`}
                              >
                                Reset to auto
                              </button>
                            ) : null}
                          </span>
                        </div>
                      );
                    }
                    const canEdit = code && EDIT_COUNTRIES.includes(code);
                    return (
                      <div key={testid} className="py-3 grid grid-cols-12 gap-2 items-center" data-testid={testid}>
                        <span className="col-span-3 text-sm text-ink flex items-center gap-2">
                          {label}
                          {canEdit ? (
                            <button
                              onClick={() => openCountryEdit(c)}
                              className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                              data-testid={`dash-country-edit-${code}`}
                            >
                              Edit
                            </button>
                          ) : null}
                        </span>
                        <span className="col-span-2 font-display text-lg text-right">{c.unique_visitors}</span>
                        <span className="col-span-2 font-display text-lg text-right">{c.visits}</span>
                        <span className="col-span-3">
                          <span className="block h-2 bg-surface w-full">
                            <span
                              className="block h-2"
                              style={{ background: "#69d2c7", width: maxVisits > 0 ? `${Math.max((c.visits / maxVisits) * 100, 1)}%` : "0%" }}
                            />
                          </span>
                        </span>
                        <span className="col-span-2 font-display text-lg text-right">{pct(c.visits, visitsTotal)}</span>
                      </div>
                    );
                  };
                  return (
                    <div className="mb-10 max-w-3xl" data-testid="dash-country-traffic">
                      <h3 className="label-mono text-muted mb-3">Traffic by country</h3>
                      <div className="border-t border-hairline divide-y divide-hairline">
                        <div className="py-2 grid grid-cols-12 gap-2 items-baseline">
                          <span className="col-span-3 label-mono text-muted">Country</span>
                          <span className="col-span-2 label-mono text-muted text-right">Unique</span>
                          <span className="col-span-2 label-mono text-muted text-right">Visits</span>
                          <span className="col-span-3" />
                          <span className="col-span-2 label-mono text-muted text-right">% Visits</span>
                        </div>
                        {adjRows.length === 0 ? (
                          <div className="py-3 text-sm text-muted" data-testid="dash-country-empty">No country data yet.</div>
                        ) : (
                          <>
                            {displayed.map((r) => countryRow(countryName(r.country), r, `dash-country-${r.country.toLowerCase()}`, r.country))}
                            {other && countryRow("Other", other, "dash-country-other")}
                            <div className="py-3 grid grid-cols-12 gap-2 items-baseline" data-testid="dash-country-total">
                              <span className="col-span-3 text-sm text-ink">Total</span>
                              <span className="col-span-2 font-display text-lg text-right">{uniqTotal}</span>
                              <span className="col-span-2 font-display text-lg text-right">{visitsTotal}</span>
                              <span className="col-span-3" />
                              <span className="col-span-2" />
                            </div>
                          </>
                        )}
                      </div>
                      <p className="mt-3 label-mono text-muted" data-testid="dash-country-since">
                        {startedAt
                          ? `Country tracking since ${startedAt} — no country data exists before this date.`
                          : "Country tracking active — data appears as visitors arrive."}
                      </p>
                    </div>
                  );
                })()}
                {section("Signup locations", [
                  metric("Landing", srcCounts["landing"] || 0, "dash-src-landing"),
                  metric("Ebook", srcCounts["ebook"] || 0, "dash-src-ebook"),
                  metric("Book", srcCounts["book"] || 0, "dash-src-book"),
                  metric("Review", srcCounts["review"] || 0, "dash-src-review"),
                  metric("Cage System", srcCounts["cage_system"] || 0, "dash-src-cage"),
                  ...otherSources.map((t) => metric(t, srcCounts[t], `dash-src-${t}`)),
                ])}
                {section("Reviews", [
                  metric("Pending", rvLive.filter((r) => r.status === "pending").length, "dash-rev-pending"),
                  metric("Approved", rvLive.filter((r) => r.status === "approved").length, "dash-rev-approved"),
                  metric("Rejected", rvLive.filter((r) => r.status === "rejected").length, "dash-rev-rejected"),
                  metric("Deleted", rv.filter((r) => r.deleted_at).length, "dash-rev-deleted"),
                ])}
                <div className="mb-10">
                  <button
                    onClick={() => setAttrResetTarget({ visits: totalVisits, leads: attributedLeadCount })}
                    className="label-mono border border-red-200 text-red-600 px-3 py-1.5 hover:bg-red-600 hover:text-white transition-colors"
                    data-testid="attr-reset-open"
                  >
                    Reset attribution data
                  </button>
                </div>
              </div>
            );
          })()
        ) : tab === "booklinks" ? (
          <div data-testid="table-booklinks">
            <h2 className="font-display text-3xl md:text-4xl mb-8">Book links</h2>
            {error && (
              <p className="mb-6 text-sm text-red-600" data-testid="booklinks-action-error">{error}</p>
            )}
            {bookLinks.length === 0 ? (
              <p className="text-muted" data-testid="booklinks-empty">No book links need attention.</p>
            ) : (
              <div className="border-t border-hairline divide-y divide-hairline">
                <div className="py-3 hidden md:grid grid-cols-12 gap-4">
                  <span className="col-span-3 label-mono text-muted">Email</span>
                  <span className="col-span-2 label-mono text-muted">Reason</span>
                  <span className="col-span-1 label-mono text-muted text-right">Downloads</span>
                  <span className="col-span-1 label-mono text-muted text-right">Sources</span>
                  <span className="col-span-2 label-mono text-muted">First — Last</span>
                  <span className="col-span-3" />
                </div>
                {bookLinks.map((b) => {
                  const reasonLabel =
                    b.reason === "rate" ? "Download rate" : b.reason === "sources" ? "Distinct sources" : "Total count";
                  return (
                    <div key={b.id} className="py-4" data-testid={`booklink-row-${b.id}`}>
                      <div className="grid grid-cols-12 gap-4 items-baseline">
                        <div className="col-span-12 md:col-span-3 text-sm text-ink break-all" data-testid={`booklink-email-${b.id}`}>
                          {b.email}
                        </div>
                        <div className="col-span-6 md:col-span-2 label-mono text-muted" data-testid={`booklink-reason-${b.id}`}>
                          {reasonLabel}
                        </div>
                        <div className="col-span-3 md:col-span-1 font-display text-lg text-right" data-testid={`booklink-downloads-${b.id}`}>
                          {b.download_count}
                        </div>
                        <div className="col-span-3 md:col-span-1 font-display text-lg text-right" data-testid={`booklink-sources-${b.id}`}>
                          {b.distinct_sources}
                        </div>
                        <div className="col-span-12 md:col-span-2 label-mono text-muted" data-testid={`booklink-window-${b.id}`}>
                          {fmt(b.first_download_at)} — {fmt(b.last_download_at)}
                        </div>
                        <div className="col-span-12 md:col-span-3 flex items-baseline justify-end gap-3 whitespace-nowrap">
                          {blRevokeConfirm === b.id ? (
                            <span className="flex items-baseline gap-3" data-testid={`booklink-revoke-strip-${b.id}`}>
                              <span className="label-mono text-muted">
                                Reader loses access; regains it by submitting the form again
                              </span>
                              <button
                                onClick={() => revokeBookLink(b)}
                                className="label-mono bg-red-600 text-white px-2 py-0.5 hover:bg-red-700 transition-colors"
                                data-testid={`booklink-revoke-confirm-${b.id}`}
                              >
                                Confirm
                              </button>
                              <button
                                onClick={() => setBlRevokeConfirm(null)}
                                className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-surface transition-colors"
                                data-testid={`booklink-revoke-cancel-${b.id}`}
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <>
                              <button
                                onClick={() => markReviewed(b)}
                                className="label-mono border border-hairline text-ink px-2 py-0.5 hover:bg-ink hover:text-white transition-colors"
                                data-testid={`booklink-review-${b.id}`}
                              >
                                Mark as reviewed
                              </button>
                              <button
                                onClick={() => setBlRevokeConfirm(b.id)}
                                className="label-mono border border-red-200 text-red-600 px-2 py-0.5 hover:bg-red-600 hover:text-white transition-colors"
                                data-testid={`booklink-revoke-${b.id}`}
                              >
                                Revoke
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {(b.downloads || []).length > 0 && (
                        <div className="mt-2 label-mono text-muted break-words" data-testid={`booklink-history-${b.id}`}>
                          {b.downloads.map((t) => fmt(t)).join(" · ")}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : tab === "sequence" ? (
          <AdminSequence csrf={csrf} />
        ) : (
          <div data-testid="table-audit">
            <h2 className="font-display text-3xl md:text-4xl mb-8">Audit log</h2>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-6">
              <input
                type="text"
                value={auditQ}
                onChange={(e) => setAuditQ(e.target.value)}
                placeholder="Search id or email..."
                className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-56"
                data-testid="audit-search"
              />
              <label className="label-mono text-muted flex items-center gap-2">
                Action
                <select
                  value={auditAction}
                  onChange={(e) => setAuditAction(e.target.value)}
                  className="bg-transparent border-b border-hairline py-1 outline-none label-mono"
                  data-testid="audit-filter-action"
                >
                  <option value="all">All</option>
                  {auditOptions("action").map((a) => <option key={a} value={a}>{a.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="label-mono text-muted flex items-center gap-2">
                Record
                <select
                  value={auditRecordType}
                  onChange={(e) => setAuditRecordType(e.target.value)}
                  className="bg-transparent border-b border-hairline py-1 outline-none label-mono"
                  data-testid="audit-filter-record-type"
                >
                  <option value="all">All</option>
                  {auditOptions("record_type").map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
              <label className="label-mono text-muted flex items-center gap-2">
                Admin
                <select
                  value={auditAdmin}
                  onChange={(e) => setAuditAdmin(e.target.value)}
                  className="bg-transparent border-b border-hairline py-1 outline-none label-mono"
                  data-testid="audit-filter-admin"
                >
                  <option value="all">All</option>
                  {auditOptions("admin").map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
              <label className="label-mono text-muted flex items-center gap-2">
                From
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/yyyy"
                  value={auditFromText}
                  onChange={(e) => setAuditFromText(e.target.value)}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-28"
                  data-testid="audit-date-from"
                />
              </label>
              <label className="label-mono text-muted flex items-center gap-2">
                To
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="dd/mm/yyyy"
                  value={auditToText}
                  onChange={(e) => setAuditToText(e.target.value)}
                  className="bg-transparent border-b border-hairline focus:border-ink py-1 text-sm outline-none w-28"
                  data-testid="audit-date-to"
                />
              </label>
            </div>
            <p className="label-mono text-muted mb-3" data-testid="audit-result-count">
              Showing {filteredAudit.length} of {(auditRows || []).length}
            </p>
            {auditRows === null ? (
              <p className="text-muted">Loading…</p>
            ) : filteredAudit.length === 0 ? (
              <p className="text-muted" data-testid="audit-empty">No audit entries match.</p>
            ) : (
              <div className="border-t border-hairline divide-y divide-hairline">
                {filteredAudit.map((a) => (
                  <div key={a.id} className="py-4 grid grid-cols-12 gap-3 md:gap-6 items-baseline" data-testid={`audit-row-${a.id}`}>
                    <div className="col-span-6 md:col-span-2 label-mono text-muted">{fmt(a.created_at)}</div>
                    <div className="col-span-6 md:col-span-1 label-mono text-muted">{a.admin}</div>
                    <div className="col-span-6 md:col-span-2 label-mono text-ink">{String(a.action || "").replace(/_/g, " ")}</div>
                    <div className="col-span-6 md:col-span-1 label-mono text-muted">{a.record_type}</div>
                    <div className="col-span-12 md:col-span-3 label-mono text-muted break-all">{a.record_id}</div>
                    <div className="col-span-12 md:col-span-3 text-sm text-ink break-words">{auditChange(a)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        </>
        )}
      </main>

      {markSentTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="lead-mark-sent-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Record a manual book send to {markSentTarget.email}? This does not send an email &mdash; it only logs a delivery you already completed.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setMarkSentTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="lead-mark-sent-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmMarkBookSent}
                className="label-mono bg-ink text-white px-4 py-2 hover:bg-black transition-colors"
                data-testid="lead-mark-sent-confirm"
              >
                Record send
              </button>
            </div>
          </div>
        </div>
      )}

      {seqStartTarget && (() => {
        const daysOld = nyDayDiffFromNow(seqStartTarget.book_sent_at);
        const bookDate = seqStartTarget.book_sent_at ? fmt(seqStartTarget.book_sent_at) : "—";
        const showFalseSentence = daysOld > 3 && seqStartFrom === 1;
        const threshold = DAY_BY_EMAIL_NUMBER[seqStartFrom];
        const showDayWarning = daysOld < threshold;
        return (
          <div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
            role="dialog"
            aria-modal="true"
            data-testid="seq-start-dialog"
          >
            <div className="bg-white max-w-md w-full p-8 border border-hairline">
              <p className="text-lg font-semibold text-ink">Start the email sequence</p>
              <p className="mt-3 text-sm text-neutral-600">
                Lead: <span className="text-ink break-all" data-testid="seq-start-email">{seqStartTarget.email}</span>
              </p>
              <p className="mt-1 text-sm text-neutral-600" data-testid="seq-start-booksent">
                Book sent: {bookDate}
              </p>
              <label className="mt-5 flex items-center gap-3 label-mono text-muted">
                Start from email
                <select
                  value={seqStartFrom}
                  onChange={(e) => setSeqStartFrom(parseInt(e.target.value, 10))}
                  className="bg-transparent border-b border-hairline py-1 outline-none label-mono text-ink"
                  data-testid="seq-start-select"
                >
                  {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              {showFalseSentence && (
                <p className="mt-4 text-sm text-red-600" data-testid="seq-start-false-sentence">
                  Email 1 opens with "The book that arrived yesterday." This lead's book arrived {daysOld} days ago. That sentence will be false.
                </p>
              )}
              {showDayWarning && (
                <p className="mt-4 text-sm text-red-600" data-testid="seq-start-day-warning">
                  Email {seqStartFrom} sends on day {threshold}. This lead's book is {daysOld} days old, so nothing will send for another {threshold - daysOld} days.
                </p>
              )}
              <p className="mt-5 text-sm text-neutral-600" data-testid="seq-start-note">
                Sends on the next daily run (07:30 America/New_York). Nothing sends now.
              </p>
              {seqStartError && (
                <p className="mt-3 text-sm text-red-600" data-testid="seq-start-error">{seqStartError}</p>
              )}
              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => setSeqStartTarget(null)}
                  className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                  data-testid="seq-start-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={startSequence}
                  disabled={seqStarting}
                  className="label-mono bg-ink text-white px-4 py-2 hover:bg-leaf transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="seq-start-confirm"
                >
                  {seqStarting ? "Starting…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {attrResetTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="attr-reset-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">Reset all attribution data?</p>
            <p className="mt-3 text-sm text-neutral-600" data-testid="attr-reset-effect">
              This deletes every visit record, including device and country visit records, and clears first-touch and last-touch attribution on {attrResetTarget.leads} lead{attrResetTarget.leads === 1 ? "" : "s"} — from all time, regardless of the Dashboard date filter. Leads, contacts, reviews and redirects are not deleted.
            </p>
            <input
              type="text"
              value={attrResetText}
              onChange={(e) => setAttrResetText(e.target.value)}
              placeholder="Type RESET to confirm"
              className="mt-4 w-full border border-hairline px-3 py-2 text-sm outline-none focus:border-ink"
              data-testid="attr-reset-input"
            />
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => { setAttrResetTarget(null); setAttrResetText(""); }}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="attr-reset-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmAttrReset}
                disabled={attrResetText !== "RESET"}
                className="label-mono bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="attr-reset-confirm"
              >
                Reset attribution data
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteContactTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="contact-delete-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Delete this contact submission? This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteContactTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="contact-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteContact}
                className="label-mono bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors"
                data-testid="contact-delete-confirm"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {resendTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="lead-resend-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Resend the free book email to {resendTarget.email}? This sends a real email using the current book-delivery workflow.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setResendTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="lead-resend-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmResendBook}
                className="label-mono bg-ink text-white px-4 py-2 hover:bg-black transition-colors"
                data-testid="lead-resend-confirm"
              >
                Resend book
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="lead-delete-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Delete {deleteTarget.email} from the leads list? This is permanent and cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="lead-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteLead}
                className="label-mono bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors"
                data-testid="lead-delete-confirm"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
      {deleteRedirectTarget && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6"
          role="dialog"
          aria-modal="true"
          data-testid="redirect-delete-dialog"
        >
          <div className="bg-white max-w-md w-full p-8 border border-hairline">
            <p className="text-lg font-semibold text-ink">
              Delete the redirect from <span className="label-mono">{deleteRedirectTarget.source}</span> to <span className="label-mono">{deleteRedirectTarget.destination}</span>? This cannot be undone.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setDeleteRedirectTarget(null)}
                className="label-mono border border-hairline px-4 py-2 hover:bg-surface transition-colors"
                data-testid="redirect-delete-cancel"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteRedirect}
                className="label-mono bg-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors"
                data-testid="redirect-delete-confirm"
              >
                Delete permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
